/*
 * Product Family section
 * ----------------------
 * Extends the theme's Global.Core base class and uses its section-scoped
 * pub/sub bus (this.sectionId / this.sub). DOM access is kept explicit
 * (querySelector) so the component is robust across theme updates.
 *
 * Elements defined here:
 *   <product-family>          orchestrator: model switch (Section Rendering
 *                             fetch of ?view=<panel-view>, or of the view the
 *                             setup row names in data-pf-view - the custom
 *                             build's family-panel-custom, Task 10), scroll-spy
 *                             sub-nav, sticky-bar visibility.
 *   <product-family-gallery>  numeric image counter (subscribes to the theme's
 *                             gallery slide-change event).
 *   <product-family-toggle>   "Learn more" description expander.
 *   <product-family-sticky>   sticky add-to-cart expand/collapse panel.
 */
const { Core, Events } = Global;

const REGIONS = ['gallery', 'pricehead', 'buybox', 'trust', 'sticky', 'sticky-cta', 'features', 'specs', 'reviews'];
// Task 33: how long a setup switch or an option change waits for its panel
// before it gives up and says so (the family-panel view is a full product
// render; slow 3G needs the room). The idle prefetch and the touch warm run
// without a limit - nothing on screen waits for them.
const PANEL_FETCH_TIMEOUT = 20000;

// Task 34: Shopify's theme-preview toolbar. Its frame posts "PreviewBar::Ping"
// to the page once it has rendered - the only sign that a toolbar is really
// drawn (its fixed wrapper is built by the storefront's script even when the
// frame inside is blocked or blank). Registered at module time so the ping
// can never arrive before a host is listening.
const PREVIEW_BAR_FRAME = 'iframe[src*="preview-bar"]';
let previewBarPinged = false;
const previewBarWaiters = new Set();
window.addEventListener('message', (e) => {
  if (e.data !== 'PreviewBar::Ping') return;
  const frame = document.querySelector(PREVIEW_BAR_FRAME);
  if (!frame || e.source !== frame.contentWindow) return;
  previewBarPinged = true;
  previewBarWaiters.forEach((fn) => fn());
});

customElements.define('product-family', class extends Core {
  render() {
    this._panelCache = {};          // view|productUrl[::variant] -> parsed Document
    this._activeIndex = this._initialActiveIndex();
    this._switching = false;

    this._models = Array.from(this.querySelectorAll('[data-element="model-option"]'));
    this._select = this.querySelector('[data-element="model-select"]');
    // The shopper's variant choice per model (index -> variant id). Returning to
    // a model re-renders it with the options they picked; before this, the buy
    // box reset to the product default while the model button and comparison
    // card kept the picked variant's price and photo (two prices, one choice).
    this._chosenVariant = {};
    // The browser restores a <select>'s value on Back/Forward, so it can name a
    // different model than the markup rendered. Make them agree before anything
    // reads it (autocomplete="off" on the control prevents most restores).
    if (this._select) this._select.value = String(this._activeIndex);
    this._cards = Array.from(this.querySelectorAll('[data-element="compare-card"]'));

    this._onModelListClick = this._onModelListClick.bind(this);
    this._onSelectChange = this._onSelectChange.bind(this);
    this._onCompareClick = this._onCompareClick.bind(this);
    this._onAnchorClick = this._onAnchorClick.bind(this);
    this._onFitLinkClick = this._onFitLinkClick.bind(this);
    this._onPfKeydown = this._onPfKeydown.bind(this);

    // One polite live region for the section (review M4): what a model or
    // option change selected, and what Add to cart did. Quiet for the first
    // moments so a restored selection on Back is not read over the page.
    this._live = document.createElement('div');
    this._live.className = 'visually-hidden';
    this._live.setAttribute('aria-live', 'polite');
    this._live.setAttribute('aria-atomic', 'true');
    this._live.setAttribute('data-pf-live', '');
    this.appendChild(this._live);
    this._liveFrom = Date.now() + 1500;
    // The buy box's product form publishes cart events under its own section
    // id (the panel view's), and the theme's bus is section-scoped: a small
    // listener with that id relays the result to the live region.
    const form = this.querySelector('product-form[section-id]');
    const relay = document.createElement('product-family-cart-relay');
    relay.setAttribute('section-id', (form && form.getAttribute('section-id')) || 'product-family-panel');
    relay.hidden = true;
    this.appendChild(relay);

    const modelList = this.querySelector('[data-element="model-list"]');
    if (modelList) modelList.addEventListener('click', this._onModelListClick);
    if (this._select) this._select.addEventListener('change', this._onSelectChange);
    // Comparison cards select their model (whole card + image button) and stay
    // in sync with the model selector.
    this.addEventListener('click', this._onCompareClick);
    // A link in the "right for you" copy to a build on this page selects it.
    this.addEventListener('click', this._onFitLinkClick);
    // Gallery thumbnails / full-screen view by keyboard; the rating's jump.
    this.addEventListener('keydown', this._onPfKeydown);
    this.addEventListener('click', (e) => {
      const jump = e.target.closest && e.target.closest('[data-pf-jump]');
      if (!jump) return;
      const dest = this.querySelector(`[data-pf-anchor="${jump.dataset.pfJump}"]`);
      if (!dest) return;
      e.preventDefault();
      dest.scrollIntoView({ block: 'start', behavior: this._smooth() });
    });
    // Remember the option control that has focus: the stock picker re-renders
    // after a variant change, and focus fell to the page body (review M4).
    this.addEventListener('focusin', (e) => {
      const el = e.target;
      if (el && el.matches && el.matches('[data-pf-options] input[type="radio"]')) {
        this._focusKey = { name: el.name, value: el.value, at: Date.now() };
      }
    });
    // A deliberate move away (a click elsewhere fires focusout; a re-render
    // that removes the control does not) forgets it.
    this.addEventListener('focusout', (e) => {
      if (e.target && e.target.matches && e.target.matches('[data-pf-options] input[type="radio"]') && !e.relatedTarget) {
        const key = this._focusKey;
        setTimeout(() => { if (this._focusKey === key && e.target.isConnected) this._focusKey = null; }, 0);
      }
    });
    // "What's included" keeps its open state when the options panel moves.
    this.addEventListener('toggle', (e) => {
      if (e.target && e.target.matches && e.target.matches('[data-pf-included]')) this._includedOpen = e.target.open;
      if (e.target && e.target.matches && e.target.matches('[data-pf-fit-disclosure]')) this._fitOpen = e.target.open;
    }, true);

    const anchorList = this.querySelector('[data-element="anchor-list"]');
    if (anchorList) anchorList.addEventListener('click', this._onAnchorClick);

    // Defined BEFORE _initStickyVisibility: its first evaluation already needs
    // to know whether the always-on mobile sticky mode applies.
    this._stickyMQ = window.matchMedia('(max-width: 991px)');

    this._initScrollSpy();
    this._initStickyVisibility();
    this._initVariantDelegation();
    this._initSeeMoreSync();
    this._initCompareSwipeGate();
    this._initGalleryAxisLock();
    this._initExpandPinch();
    this._syncModelTabindex();
    this._initModelKeys();
    this._prefetchPanels();
    this._restoreSelection();
    this._initCompareDisclosure();
    this._initCompareNav();
    this._initCompareRow();
    this._syncLiveRating();

    // Bring the selected model's card into view once the compare slider has
    // initialised (it always starts at slide 0; the active model may be a
    // later card, especially on mobile's one-card view).
    this._whenCompareSliderReady(() => this._showCardInSlider(this._activeIndex));

    // Equalize the compare cards' row zones after first paint (fonts/layout
    // settled), and again whenever the viewport changes wrap points.
    requestAnimationFrame(() => this._equalizeCardRows());
    window.addEventListener('load', () => this._equalizeCardRows(), { once: true });
    this._equalizeResizeHandler = () => {
      clearTimeout(this._equalizeTimer);
      this._equalizeTimer = setTimeout(() => {
        this._tagMultilineOptions();
        this._equalizeCardRows();
        this._syncStickySpace();
        this._fitModelSelect();
      }, 200);
    };
    window.addEventListener('resize', this._equalizeResizeHandler, { passive: true });

    // Shrink overlong model titles so the selected one fits the sub-nav
    // dropdown. Re-measured once fonts have actually loaded.
    requestAnimationFrame(() => this._fitModelSelect());
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this._fitModelSelect());
    }

    // Mobile sticky Add to cart: re-evaluate the bar (and the space reserved for
    // it at the bottom of the page) whenever the viewport crosses the breakpoint.
    this._onStickyMQ = () => {
      this._syncStickySpace();
      if (this._evaluateSticky) this._evaluateSticky();
      this._syncSummary();
    };
    if (this._stickyMQ.addEventListener) this._stickyMQ.addEventListener('change', this._onStickyMQ);
    // Both, explicitly: with the drawer switched off there is no drawer to
    // populate, but the always-on bar still needs its height reserved.
    requestAnimationFrame(() => {
      this._syncStickyDrawer();
      // Task 30: the bar's first evaluation lands here only when its
      // stylesheet had not applied while _initStickyVisibility ran.
      if (!this._stickyEvaluated && this._evaluateSticky) { this._stickyEvaluated = true; this._evaluateSticky(); }
      this._syncStickySpace();
    });

    // The variant options are a grid item of the model list; `order` slots them
    // under the selected model. No Step 1 / Step 2 headings any more.
    this._syncOptionsPlacement();

    // Step 2 option buttons: a wrapped label turns the full-pill button into
    // an egg — tag wrapped buttons @multiline so the CSS switches those to
    // rounded rectangles. The buybox subtree is replaced on model switches
    // and by the provider on variant changes, so retag on any subtree change
    // (class toggles don't fire childList mutations, so this can't loop).
    requestAnimationFrame(() => {
      this._tagMultilineOptions();
      this._annotateOptions();
    });
    this._multilineObserver = new MutationObserver(() => {
      this._annotateOptionsNow();
      this._labelOptionGroups();
      this._syncSummary();
      // at once, not only in the debounced pass below: focus sat on the page
      // body until then (150ms-2s on phones) after an arrow-key option change
      this._restoreOptionFocus();
      clearTimeout(this._multilineTimer);
      this._multilineTimer = setTimeout(() => {
        this._tagMultilineOptions();
        this._annotateOptions();
        this._restoreOptionFocus();
        this._keyGallery();
        if (this._includedOpen) this.querySelectorAll('[data-pf-included]').forEach((d) => { if (!d.open) d.open = true; });
        if (this._fitOpen) this.querySelectorAll('[data-pf-fit-disclosure]').forEach((d) => { if (!d.open) d.open = true; });
      }, 60);
    });
    this._multilineObserver.observe(this, { childList: true, subtree: true });

    // The theme's sticky header has no CSS var exposing its height, and a one-time
    // measurement is wrong whenever other bars scroll away or the header hides —
    // so track its ACTUAL bottom edge and pin the sub-nav directly beneath it.
    const themeHeader = document.querySelector('.\\#header');
    if (themeHeader) {
      let ticking = false;
      const updateOffset = () => {
        const bottom = Math.max(0, Math.round(themeHeader.getBoundingClientRect().bottom));
        this.style.setProperty('--pf-subnav-top', bottom + 'px');
        this._syncGalleryProgress();
        ticking = false;
      };
      updateOffset();
      this._headerOffsetHandler = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(() => {
            updateOffset();
            // Desktop (Task 33): the bar's line is the pinned header + sub-nav
            // plus the button (_desktopStickyLine), and the header's edge
            // moves, so the rule is re-read here, on every scroll frame.
            if (this._evaluateSticky && !(this._stickyMQ && this._stickyMQ.matches)) this._evaluateSticky();
          });
        }
      };
      window.addEventListener('scroll', this._headerOffsetHandler, { passive: true });
      window.addEventListener('resize', this._headerOffsetHandler, { passive: true });
    }
    this._initStickyGallery();
    this._syncTagline = this._syncTagline.bind(this);
    this._syncTagline();
    window.addEventListener('resize', this._syncTagline, { passive: true });
  }

  // Phones hide the hero and, with it, the model's tagline (Oct 1 re-critique
  // Top 2): show the tagline under the title whenever the section above this
  // one renders nothing.
  _syncTagline() {
    if (!this.querySelector('[data-pf-tagline]')) return;
    const host = this.closest('.shopify-section');
    const hero = host && host.previousElementSibling;
    this.toggleAttribute('pf-hero-hidden', !hero || hero.getBoundingClientRect().height < 1);
  }

  // Desktop: the gallery stays in view beside the buy column (Oct 1
  // re-critique H1: the column sat empty beside Add to cart) - only while it
  // fits under the header and sub-nav; taller than that, it scrolls as usual.
  _initStickyGallery() {
    const gallery = this.querySelector('[data-pf-region="gallery"]');
    if (!gallery || !window.ResizeObserver) return;
    const desk = window.matchMedia('(min-width: 992px)');
    const header = document.querySelector('.\\#header');
    const subnav = this.querySelector('[data-element="subnav"]');
    const media = this.querySelector('.\\#product-family-overview-media') || gallery;
    // The viewport height the pin is judged by (Task 32): latched at load and
    // re-read only when the width changes or the height jumps by 150px or
    // more - a phone's toolbars sliding away and back move innerHeight by
    // about 80px on every scroll reversal, and must not flap the pin. Never
    // taller than the screen: no phone's window is, and a device emulator
    // can report a window several times its screen for a moment mid-load.
    const viewportH = () => Math.min(Math.max(window.innerHeight, document.documentElement.clientHeight), (window.screen && window.screen.height) || Infinity);
    this._galleryVH = viewportH();
    this._galleryVW = window.innerWidth;
    const check = () => {
      const subH = subnav ? Math.round(subnav.getBoundingClientRect().height) : 0;
      this.style.setProperty('--pf-subnav-h', subH + 'px');
      const headH = header ? header.getBoundingClientRect().height : 0;
      const fits = gallery.getBoundingClientRect().height <= window.innerHeight - headH - subH - 16;
      this.toggleAttribute('pf-gallery-sticky', desk.matches && fits);
      // Phones and tablets (Task 24, Garrett: the gallery stays "sticky until
      // the user scrolls down to the setup comparison section"): the photo
      // pins under the sub-nav through the overview while that leaves 300px
      // or more above the buy bar to choose in - so not in landscape. Judged
      // on the strip it pins as (Task 32: 120px, not the arrival size) and
      // with hysteresis: pinned at 300px of room, let go only under 240.
      const vh = viewportH();
      if (window.innerWidth !== this._galleryVW || Math.abs(vh - this._galleryVH) >= 150) {
        this._galleryVH = vh;
        this._galleryVW = window.innerWidth;
      }
      // The bar's measured height (Task 24); before it is measured, the
      // stylesheet's own reserve for it (88px, body.pf-sticky-space) stands
      // in, so an early check never pins a short phone the real bar would not.
      const onBar = this.hasAttribute('sticky-mobile') && this._stickyMQ && this._stickyMQ.matches;
      const barH = parseFloat(document.documentElement.style.getPropertyValue('--pf-sticky-space')) || (onBar ? 88 : 0);
      // The collapse's sizes first (the gallery may have changed): while the
      // collapse is on, the strip is judged at its compact height; when it is
      // off - no stylesheet sizes, or a slide taller than the square frame -
      // the photo pins at its own height and is judged by that.
      this._measureGalleryCollapse();
      const stripH = this._galleryDelta > 0 ? (parseFloat(getComputedStyle(media).getPropertyValue('--pf-gallery-min')) || 120) : media.getBoundingClientRect().height;
      const room = this._galleryVH - headH - subH - stripH - barH;
      let pin = !desk.matches && this.hasAttribute('pf-gallery-pin');
      if (desk.matches || room < 240) pin = false;
      else if (room >= 300) pin = true;
      this.toggleAttribute('pf-gallery-pin', pin);
      // The collapse's progress is written in the next frame, never from
      // inside this observer.
      this._requestGalleryProgress();
    };
    this._galleryFitCheck = check;
    this._galleryFitRO = new ResizeObserver(check);
    this._galleryFitRO.observe(gallery);
    window.addEventListener('resize', check, { passive: true });
    // Crossing the breakpoint re-measures the collapse (its sizes differ per
    // width); the progress follows in the next frame (Task 32).
    this._onGalleryMQ = () => check();
    if (this._stickyMQ && this._stickyMQ.addEventListener) this._stickyMQ.addEventListener('change', this._onGalleryMQ);
    check();
    // Keyboard focus never lands under the pinned photo (WCAG 2.4.11): a
    // control in the buy column it covers scrolls down clear of it.
    this.addEventListener('focusin', (e) => {
      if (!this.hasAttribute('pf-gallery-stuck')) return;
      const t = e.target;
      if (!t || !t.closest || !t.closest('.\\#product-family-overview-buy')) return;
      const r = t.getBoundingClientRect();
      const floor = media.getBoundingClientRect().bottom + 8;
      if (r.top < floor && r.bottom > 0) window.scrollBy({ top: r.top - floor, left: 0, behavior: 'instant' });
    });
  }

  // Variant-change reactions, via native DOM event delegation ('change' bubbles).
  // NOTE: do NOT use pub/sub here — this theme's pub/sub delivers strictly within a
  // matching section-id scope (verified live; the { global: true } option does not
  // cross scopes), and the buybox publishes under the 'product-family-panel' scope
  // while this element carries the collection section's id.
  //  1. Regions: re-fetch + swap gallery (curated image set), specs (Components
  //     group), price head and the sticky bar for the selected variant.
  //  2. Variant id: set the hidden name="id" inputs immediately. The theme's provider
  //     re-renders them after its (async) section fetch; setting them synchronously
  //     closes the race where a fast Add-to-cart submits the previous variant.
  //  3. Variant gaps: when the clicked combination has no variant, the PF
  //     provider (allow-unavailable-variants) re-renders the buybox into the
  //     stock disabled-"Unavailable" state; follow it via the listeners'
  //     'update' events and mirror it onto the sticky bar (details below).
  _initVariantDelegation() {
    // Seed the gap-follower (listener 3 below) with the initially rendered
    // variant, so provider cache republishes on load don't read as changes.
    this._lastVariantSync = this._currentFormVariantId();

    // CAPTURE phase, deliberately: when the provider already has the target variant
    // cached (e.g. toggling back to the default), it re-renders the selector
    // synchronously in microtasks DURING event dispatch — by the bubble phase the
    // clicked radio is detached and its ancestry checks fail. Capture runs first.
    this.addEventListener('change', (e) => {
      const input = e.target;
      if (!input || !input.matches || !input.matches('input[type="radio"]')) return;
      if (!input.closest('[data-pf-region="buybox"]') && !input.closest('[data-pf-options]')) return;

      const variantId = input.dataset.variantId;

      if (variantId) {
        // Re-render the variant-dependent regions (gallery: curated image set;
        // specs: the Components group) for the selected variant.
        clearTimeout(this._gallerySyncTimer);
        this._gallerySyncTimer = setTimeout(() => this._syncVariantRegions(variantId), 150);
      }

      if (variantId) {
        this._lastVariantSync = String(variantId);
        this._rememberVariant(variantId);
        this.querySelectorAll('[data-pf-region="buybox"] input[name="id"], [data-pf-options] input[name="id"]').forEach((idInput) => {
          idInput.value = variantId;
        });
        this._updateCardVariantSpecs(variantId);
      }
    }, true);

    // 3. Variant GAPS — option combinations with no variant (e.g. Fathom
    //    Standard Complete: Strata / 105mm Dad Bod 80a does not exist). The
    //    stock radio snippet renders no data-variant-id on those values, so
    //    the handler above has nothing to act on. Merchant-decided UX
    //    (2026-09-02): gaps are ENTERABLE and SIGNALED, never carted — the PF
    //    provider renders allow-unavailable-variants (see
    //    snippets/product-family-panel.liquid), so a gap click re-renders the
    //    buybox into the theme's stock unavailable state: checked pills fade,
    //    the buy-box button goes disabled "Unavailable", and the form's
    //    hidden id EMPTIES. Every neighbouring pill stays clickable, so any
    //    next click recovers.
    //    Those re-renders land as listener updates, NOT 'change' events, so
    //    follow them here: every product-variant-listener dispatches a
    //    bubbling 'update' CustomEvent after re-rendering. Read the settled
    //    id off the form's hidden input at event time (never off the event —
    //    a later click may already have superseded it):
    //    - EMPTY id → mirror the unavailable state onto the sticky bar. Its
    //      CTA is not a provider listener; left alone it would stay an active
    //      Add to cart submitting an empty id — the live "Parameter Missing
    //      or Invalid: items" failure this replaces.
    //    - REAL id → sync the PF regions (also covers a provider-side
    //      correction if smart select is ever re-enabled for PF).
    //    _lastVariantSync makes the ordinary direct-variantId path (already
    //    synced above) a no-op here.
    this.addEventListener('update', () => {
      const variantId = this._currentFormVariantId();
      if (!variantId) {
        // Re-run on EVERY update while unavailable (no dedupe): the provider
        // re-renders its listeners one by one, and the buy-box button this
        // mirrors from only exists once the ATC listener's own update has
        // landed — the first update of the batch is too early. Idempotent.
        this._lastVariantSync = variantId;
        this._setStickyUnavailable();
        return;
      }
      if (variantId === this._lastVariantSync) return;
      this._lastVariantSync = variantId;
      this._rememberVariant(variantId);
      clearTimeout(this._gallerySyncTimer);
      this._gallerySyncTimer = setTimeout(() => this._syncVariantRegions(variantId), 150);
      this._updateCardVariantSpecs(variantId);
    });
  }

  // Mirror the buy box's unavailable state onto the sticky bar's CTA (used by
  // both the desktop scroll-triggered bar and the mobile pinned bar — same
  // nodes). The sticky regions are normally swapped by _syncVariantRegions,
  // which needs a REAL variant id to fetch, so the no-variant state has to be
  // applied client-side: disable the button and copy the buy box's own
  // server-rendered label ("Unavailable", localized) over its content.
  // Temporary by construction — the next valid selection swaps the whole
  // sticky-cta region in fresh from the server.
  _setStickyUnavailable() {
    const cta = this.querySelector('[data-pf-region="sticky-cta"] [data-element="to-cart-button"]');
    if (!cta) return;
    cta.disabled = true;
    const buyboxBtn = this.querySelector('[data-pf-region="buybox"] button[disabled]');
    const label = buyboxBtn && buyboxBtn.textContent.trim();
    if (label) cta.textContent = label;
  }

  // The authoritative selected-variant id: the ACTIVE model's form-associated
  // hidden input, wherever the travelling options node currently sits (in the
  // buybox card or moved into the sticky drawer). Empty string when the
  // selection has no variant (gap state, smart selector disabled).
  _currentFormVariantId() {
    const idInput = this.querySelector('[data-pf-region="buybox"] input[name="id"], [data-pf-options] input[name="id"]');
    return (idInput && idInput.value) || '';
  }

  // The CSS min-heights on the card zones assume desktop wrap points; on
  // narrow screens long values (trucks/wheels names, installment copy) wrap to
  // different line counts per card and the rows drift out of alignment. This
  // measures each zone across ALL cards (custom-complete cards included) and
  // pins every card's zone to the tallest, so rows line up while swiping.
  // Compare each Step 2 option label's rendered height to its line height;
  // more than one line means the pill shape can't work — tag it for the CSS.
  _tagMultilineOptions() {
    this.querySelectorAll('.\\#product-options-radio-item-body').forEach((body) => {
      const title = body.querySelector('.\\#product-options-radio-item-title') || body;
      const lineHeight = parseFloat(getComputedStyle(title).lineHeight) || 24;
      const lines = Math.round(title.getBoundingClientRect().height / lineHeight);
      body.classList.toggle('@multiline', lines > 1);
    });
  }

  _equalizeCardRows() {
    const cards = Array.from(this.querySelectorAll('.\\#product-family-card'));
    if (cards.length < 2) return;
    // Under the rail (Tasks 35/36) subgrid aligns the rows; an inline
    // min-height from an earlier pass (before the rail was built) would only
    // pad them, so clear it and stop.
    if (this.hasAttribute('pf-rail')) {
      cards.forEach((c) => c.querySelectorAll('[style*="min-height"]').forEach((el) => { el.style.minHeight = ''; }));
      return;
    }
    const zones = [
      '.\\#product-family-card-title-row',
      '.\\#product-family-card-price',
      '[data-pf-vspec="trucks"]',
      '[data-pf-vspec="wheels"]',
      '[data-pf-vspec="bearings"]',
      '[data-pf-vspec="bushings"]',
    ];
    const level = (els) => {
      if (els.length < 2) return;
      let max = 0;
      els.forEach((el) => {
        el.style.minHeight = '';
        const h = el.getBoundingClientRect().height;
        if (h > max) max = h;
      });
      els.forEach((el) => {
        el.style.minHeight = Math.ceil(max) + 'px';
      });
    };
    zones.forEach((sel) => {
      level(cards
        .map((c) => c.querySelector(sel))
        .filter((el) => el && !el.hidden));
    });
    // "Only differences" rows ([data-pf-diffs]): the same spec line can wrap
    // differently per card, so level each line by its label across cards.
    const diffGroups = new Map();
    cards.forEach((c) => {
      c.querySelectorAll('[data-pf-diffs] .\\#product-family-specs-row').forEach((row) => {
        const label = row.querySelector('dt');
        const key = (label ? label.textContent : '').trim();
        if (!key) return;
        if (!diffGroups.has(key)) diffGroups.set(key, []);
        diffGroups.get(key).push(row);
      });
    });
    diffGroups.forEach((rows) => level(rows));
  }

  // The compare slider (slideshow-inline-blaze) initialises asynchronously —
  // poll briefly for its Blaze instance before positioning it.
  _whenCompareSliderReady(cb, tries = 0) {
    const el = this.querySelector('.\\#product-family-compare slideshow-inline-blaze');
    if (el && el.slider) return cb();
    if (tries > 60) return;
    requestAnimationFrame(() => this._whenCompareSliderReady(cb, tries + 1));
  }

  // Slide the Model Options row so the card for the given model index is in
  // view. Uses Blaze's own states (each state's page = [firstSlide, lastSlide]
  // currently visible) so it works for any slides-per-view, including the
  // fractional mobile peek. No-op when the card is already visible.
  _showCardInSlider(index) {
    // The rail row (Tasks 35/36): scroll the card beside the rail if it is
    // not fully in view; the slider is hidden.
    if (this._compareRow) {
      const row = this._compareRow;
      const card = this._compareCardsOrder.find((c) => Number(c.dataset.pfIndex) === index);
      if (!card) return;
      const rail = row.querySelector('.\\#product-family-compare-rail');
      const inset = rail ? rail.offsetWidth + (parseFloat(getComputedStyle(row).columnGap) || 0) : 0;
      const l = card.offsetLeft - row.scrollLeft;
      const visible = l >= inset - 1 && l + card.offsetWidth <= row.clientWidth + 1;
      if (!visible) row.scrollTo({ left: Math.max(0, card.offsetLeft - inset), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return;
    }
    const el = this.querySelector('.\\#product-family-compare slideshow-inline-blaze');
    const slider = el && el.slider;
    if (!slider || !slider.states || slider.isStatic) return;
    const slides = Array.from(slider.slides);
    const target = slides.findIndex((s) => {
      const card = s.matches('[data-element="compare-card"]') ? s : s.querySelector('[data-element="compare-card"]');
      return card && Number(card.dataset.pfIndex) === index;
    });
    if (target < 0) return;
    let stateIdx = slider.states.findIndex((st) => st.page[0] <= target && target <= st.page[1]);
    if (stateIdx < 0) stateIdx = Math.min(target, slider.states.length - 1);
    const diff = stateIdx - slider.stateIndex;
    if (diff > 0) slider.next(diff);
    else if (diff < 0) slider.prev(-diff);
  }

  // Blaze translates the track on EVERY touchmove by the horizontal delta and
  // commits a slide change at just 10px — so a vertical page scroll with
  // natural finger wobble wiggles the tiles and can flip slides. Gate the
  // slider's touchmove stream (capture phase, so we run before the track's own
  // listeners): swallow moves until ~12px of travel decides the gesture, then
  // keep swallowing if it is vertical-ish. Only clearly horizontal gestures
  // reach Blaze. Listeners are passive — native pan-y scrolling is untouched.
  _initCompareSwipeGate() {
    const zone = this.querySelector('.\\#product-family-compare');
    if (!zone) return;
    let startX = 0;
    let startY = 0;
    let decided = false;
    let blockDrag = false;
    zone.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      decided = false;
      blockDrag = false;
    }, { capture: true, passive: true });
    zone.addEventListener('touchmove', (e) => {
      if (!decided) {
        const dx = Math.abs(e.touches[0].clientX - startX);
        const dy = Math.abs(e.touches[0].clientY - startY);
        if (dx + dy < 12) {
          e.stopPropagation();
          return;
        }
        decided = true;
        // Horizontal only when clearly so (dx > ~1.4x dy); ties go to scrolling.
        blockDrag = dy >= dx * 0.7;
      }
      if (blockDrag) e.stopPropagation();
    }, { capture: true, passive: true });
  }

  // The gallery slider wobbles under a vertical page scroll for the same
  // reason the compare row did (see _initCompareSwipeGate): Blaze ignores the
  // Y axis entirely — every touchmove translates the track by the horizontal
  // delta, and ±10px of drift at finger-lift flips the slide. And in the other
  // direction, a deliberate horizontal swipe still lets the browser pan the
  // page vertically, so both axes move at once. This gate makes gallery
  // touches single-axis: the first ~12px of travel decides the gesture, then
  // the losing axis is locked out for the rest of that touch.
  //
  // Delegated from the host in the CAPTURE phase because the gallery region —
  // slider included — is replaced on every model/variant swap; a listener on
  // the slider itself would die on the first switch. Scoped to the INLINE
  // slider only (the expand modal's zoom/pan is _initExpandPinch's, and a
  // second finger hands the touch off to it untouched). touchmove is
  // passive:false — the horizontal lock needs preventDefault to stop native
  // page scrolling, guarded by e.cancelable so a scroll the browser already
  // owns degrades to today's behavior instead of throwing.
  _initGalleryAxisLock() {
    const st = { active: false, decided: false, horizontal: false, x: 0, y: 0 };
    this._onGalleryTouchStart = (e) => {
      st.active = false;
      if (e.touches.length !== 1) return;
      const zone = e.target.closest && e.target.closest('[data-pf-region="gallery"] product-gallery-slider');
      if (!zone || !this.contains(zone)) return;
      st.active = true;
      st.decided = false;
      st.horizontal = false;
      st.x = e.touches[0].clientX;
      st.y = e.touches[0].clientY;
    };
    this._onGalleryTouchMove = (e) => {
      if (!st.active) return;
      if (e.touches.length !== 1) { st.active = false; return; }
      if (!st.decided) {
        const dx = Math.abs(e.touches[0].clientX - st.x);
        const dy = Math.abs(e.touches[0].clientY - st.y);
        if (dx + dy < 12) {
          // Undecided: Blaze sees nothing (no track wiggle), the page may scroll.
          e.stopPropagation();
          return;
        }
        st.decided = true;
        // Same tie-break as the compare gate: horizontal only when clearly so
        // (dx > ~1.4x dy); ties go to scrolling.
        st.horizontal = dy < dx * 0.7;
      }
      if (st.horizontal) {
        // Strict left-right: the browser never starts a vertical page scroll.
        if (e.cancelable) e.preventDefault();
      } else {
        // Strict up-down: Blaze never sees the gesture, the track never moves.
        e.stopPropagation();
      }
    };
    this._onGalleryTouchEnd = () => { st.active = false; };
    this.addEventListener('touchstart', this._onGalleryTouchStart, { capture: true, passive: true });
    this.addEventListener('touchmove', this._onGalleryTouchMove, { capture: true, passive: false });
    this.addEventListener('touchend', this._onGalleryTouchEnd, { capture: true, passive: true });
    this.addEventListener('touchcancel', this._onGalleryTouchEnd, { capture: true, passive: true });
  }

  /* ---------------- Model list: roving tabindex (radiogroup) ---------------- */

  // The model list is ONE tab stop, not one per model. Without this every model
  // button is separately tabbable, and because the options panel is positioned
  // by CSS `order` (DOM order != visual order) focus jumps BACKWARDS past it:
  // selected model -> the models below the panel -> back up to the panel.
  // A single tab stop collapses the order to
  //   selected model -> options -> Add to cart
  // which is exactly the visual order. WCAG 2.4.3.
  //
  // It also makes the existing role="radiogroup" / role="radio" markup honest:
  // those roles have always promised arrow-key navigation that was never
  // implemented, so screen readers announced "radio group, 1 of 3" while the
  // arrow keys did nothing.
  _syncModelTabindex(index) {
    const active = typeof index === 'number' ? index : this._activeIndex;
    this._models.forEach((btn) => {
      btn.tabIndex = Number(btn.dataset.pfIndex) === active ? 0 : -1;
    });
  }

  _initModelKeys() {
    const list = this.querySelector('[data-element="model-list"]');
    if (!list) return;
    const KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
    this._onModelKeydown = (e) => {
      if (!KEYS.includes(e.key)) return;
      const btn = e.target.closest && e.target.closest('[data-element="model-option"]');
      if (!btn) return;
      // Work in ARRAY position, not data-pf-index: a Product block with no
      // product selected leaves a gap in the index sequence.
      const pos = this._models.indexOf(btn);
      if (pos < 0) return;
      const last = this._models.length - 1;
      let next;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = pos === last ? 0 : pos + 1;
      else next = pos === 0 ? last : pos - 1;
      const target = this._models[next];
      if (!target || target === btn) return;
      e.preventDefault(); // arrows would otherwise scroll the page
      target.focus();
      // Arrows SELECT, per the radiogroup pattern. Safe despite the switch being
      // expensive: _selectModel returns early while _switching, and _fetchPanel
      // caches per (product, variant), so arrowing back and forth costs nothing.
      this._selectModel(Number(target.dataset.pfIndex), target.dataset.pfUrl, target);
    };
    list.addEventListener('keydown', this._onModelKeydown);
  }

  /* ---------------- Pinch-to-zoom in the expanded gallery ---------------- */

  // The stock <product-gallery-expand-zoom> deliberately binds NOTHING on
  // touch: its render() is `MediaQueries.MOBILE.matches || (...)`, which
  // short-circuits the whole initialisation at <=991px. Its zoom is a
  // mouse-oriented contain<->cover toggle panned with mousemove.
  //
  // On product pages that gap is masked by the Variant Image Wizard app, which
  // ships its own Swiper gallery — but VIW only runs on product templates, so
  // on a Family collection page the expanded image is simply inert. This adds
  // real pinch/pan/double-tap, delegated from the host so it survives every
  // gallery region swap, and scoped to this element so PDP galleries (VIW's or
  // the theme's) are untouched.
  _initExpandPinch() {
    if (!window.matchMedia('(pointer: coarse)').matches && !('ontouchstart' in window)) return;

    const MAX = 4;
    const st = this._pinch = {
      el: null, img: null, scale: 1, x: 0, y: 0,
      startDist: 0, startScale: 1, startX: 0, startY: 0, panX: 0, panY: 0,
      lastTap: 0, pinching: false,
    };

    const zoomEl = (e) => {
      const el = e.target && e.target.closest && e.target.closest('product-gallery-expand-zoom');
      return el && this.contains(el) ? el : null;
    };
    // offsetWidth/Height are LAYOUT sizes — unaffected by the transform we are
    // about to write, unlike getBoundingClientRect().
    const clamp = () => {
      if (!st.img) return;
      const maxX = Math.max(0, (st.img.offsetWidth * st.scale - st.img.offsetWidth) / 2);
      const maxY = Math.max(0, (st.img.offsetHeight * st.scale - st.img.offsetHeight) / 2);
      st.x = Math.min(maxX, Math.max(-maxX, st.x));
      st.y = Math.min(maxY, Math.max(-maxY, st.y));
    };
    // translate BEFORE scale: the pan offset then stays in unscaled screen px.
    const apply = () => {
      if (st.img) st.img.style.transform = `translate(${st.x}px, ${st.y}px) scale(${st.scale})`;
    };
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    this._resetPinch = () => {
      this.querySelectorAll('product-gallery-expand-zoom').forEach((z) => {
        const img = z.querySelector('.\\#product-gallery-expand-zoom-img');
        if (img) img.style.transform = '';
        z.classList.remove('!zoomed', '!pinching');
      });
      st.el = null; st.img = null; st.scale = 1; st.x = 0; st.y = 0; st.pinching = false;
    };

    this._onPinchStart = (e) => {
      const z = zoomEl(e);
      if (!z) return;
      if (st.el && st.el !== z) this._resetPinch();
      st.el = z;
      st.img = z.querySelector('.\\#product-gallery-expand-zoom-img');
      if (!st.img) { st.el = null; return; }

      if (e.touches.length === 2) {
        st.pinching = true;
        st.startDist = dist(e.touches);
        st.startScale = st.scale;
        z.classList.add('!pinching');
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.touches.length === 1) {
        const now = Date.now();
        if (now - st.lastTap < 300) {
          st.scale = st.scale > 1 ? 1 : 2.5;
          st.x = 0; st.y = 0;
          clamp(); apply();
          z.classList.toggle('!zoomed', st.scale > 1);
          st.lastTap = 0;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        st.lastTap = now;
        st.startX = e.touches[0].clientX;
        st.startY = e.touches[0].clientY;
        st.panX = st.x; st.panY = st.y;
      }
    };

    this._onPinchMove = (e) => {
      if (!st.el || !st.img) return;
      if (st.pinching && e.touches.length === 2) {
        const d = dist(e.touches);
        if (st.startDist > 0) {
          st.scale = Math.min(MAX, Math.max(1, st.startScale * (d / st.startDist)));
          clamp(); apply();
          st.el.classList.toggle('!zoomed', st.scale > 1);
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.touches.length === 1 && st.scale > 1) {
        st.x = st.panX + (e.touches[0].clientX - st.startX);
        st.y = st.panY + (e.touches[0].clientY - st.startY);
        clamp(); apply();
        // Capture phase + stopPropagation: keeps the Blaze track from swiping
        // the slide out from under a zoomed image.
        e.preventDefault();
        e.stopPropagation();
      }
    };

    this._onPinchEnd = () => {
      if (!st.el) return;
      st.pinching = false;
      st.el.classList.remove('!pinching');
      if (st.scale <= 1.01) {
        st.scale = 1; st.x = 0; st.y = 0;
        apply();
        st.el.classList.remove('!zoomed');
      }
    };
    this._onPinchModalClose = () => this._resetPinch();

    this.addEventListener('touchstart', this._onPinchStart, { capture: true, passive: false });
    this.addEventListener('touchmove', this._onPinchMove, { capture: true, passive: false });
    this.addEventListener('touchend', this._onPinchEnd, { capture: true, passive: true });
    this.addEventListener('modal-close', this._onPinchModalClose);
  }

  // One "See more" toggles EVERY card's details together — the cards are
  // equal-height, so a single card expanding stretches all borders anyway.
  // 'toggle' does not bubble; a capture listener on this ancestor still sees it.
  // Assigning .open the value it already has fires no event, so the fan-out
  // converges without loops.
  _initSeeMoreSync() {
    this.addEventListener('toggle', (e) => {
      const details = e.target;
      if (!details.matches || !details.matches('.\\#product-family-card-more')) return;
      this.querySelectorAll('.\\#product-family-card-more').forEach((d) => {
        if (d !== details) d.open = details.open;
      });
    }, true);
  }

  // Re-render the variant-dependent regions (gallery + specs + sticky bar) for
  // the selected variant. Last request wins: a stale response is dropped on
  // rapid toggling.
  async _syncVariantRegions(variantId) {
    const model = this._models[this._activeIndex];
    const url = model && model.dataset.pfUrl;
    if (!url) return;
    const token = (this._gallerySyncToken = (this._gallerySyncToken || 0) + 1);
    let doc = await this._fetchPanel(url, variantId, { timeout: PANEL_FETCH_TIMEOUT });
    if (token !== this._gallerySyncToken) return;
    let mirrored = false;
    if (!doc) {
      // Task 33: the form's hidden id has already moved to the chosen
      // variant (written at once in _initVariantDelegation) while the bar
      // still shows the previous price, so the variant is mirrored onto the
      // bar from the product JSON right away - never an always-on Add to
      // cart with a stale price - and the panel is tried once more a second
      // later (a dropped request on a flaky connection usually works the
      // second time); a swap then overwrites the mirror.
      this._mirrorVariantToBar(url, variantId);
      mirrored = true;
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (token !== this._gallerySyncToken) return;
      doc = await this._fetchPanel(url, variantId, { timeout: PANEL_FETCH_TIMEOUT });
      if (!doc || token !== this._gallerySyncToken) return;
    }
    this._swapRegion('gallery', doc);
    this._swapRegion('specs', doc);
    // The price head, trust line and sticky bar sit OUTSIDE the buybox
    // provider's subtree, so the provider never re-renders them on variant
    // change — swap the whole regions (the fetched doc is rendered with
    // ?variant=; the trust line's returns claim is per variant: sale variants
    // are final sale). The options block is NOT swapped here: it is a provider
    // listener and re-renders itself.
    this._swapRegion('pricehead', doc);
    this._swapRegion('trust', doc);
    this._swapRegion('sticky', doc);
    this._swapRegion('sticky-cta', doc);
    if (this._evaluateSticky) this._evaluateSticky();
    this._keyGallery();
    // The mirror already said the price; a late swap changes nothing audible.
    if (!mirrored) this._announceSelection(variantId, false);
  }

  // Sync the ACTIVE comparison card's build-spec rows (Trucks / Wheels / Bearings,
  // variant metafields complete.*) to the selected variant, using the JSON map the
  // card embeds server-side. Rows with no value for the variant are hidden.
  _updateCardVariantSpecs(variantId) {
    const card = this._cards.find((c) => Number(c.dataset.pfIndex) === this._activeIndex);
    if (!card) return;
    const mapEl = card.querySelector('[data-pf-vspecs-map]');
    if (!mapEl) return;
    let map;
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (err) {
      return;
    }
    const specs = map[variantId];
    if (!specs) return;
    this._syncSelectedPrice(specs);
    // The selected row's spec line follows the variant too - only the
    // generated line; a merchant-written line stays as written.
    const row = this._models.find((b) => Number(b.dataset.pfIndex) === this._activeIndex);
    const lineEl = row && row.querySelector('[data-pf-model-line-auto]');
    if (lineEl) {
      const t = String(specs.trucks || '').split(' (')[0].replace(' 50°', '').trim();
      const w = String(specs.wheels || '').split(' (')[0].replace(/^Orangatang /, '').trim();
      if (t && t !== '-' && w && w !== '-') {
        const unit = (text) => {
          const u = document.createElement('span');
          u.className = '#product-family-model-unit';
          u.textContent = text;
          return u;
        };
        lineEl.replaceChildren(document.createTextNode(t), unit(' trucks'), document.createTextNode(' · ' + w), unit(' wheels'));
      }
    }
    // "-" is the merchant's "ships without" value; print it as words, the same
    // server-rendered label the card uses ([data-pf-none]).
    const list = card.querySelector('[data-pf-vspecs]');
    const noneLabel = list && list.dataset.pfNone;
    card.querySelectorAll('[data-pf-vspec]').forEach((row) => {
      const value = specs[row.dataset.pfVspec];
      const valEl = row.querySelector('[data-pf-vspec-value]');
      if (value) {
        if (valEl) valEl.textContent = (value === '-' && noneLabel) ? noneLabel : value;
        row.hidden = false;
      } else {
        row.hidden = true;
      }
    });
    // Card image follows the selected variant's main image.
    if (specs.image) {
      const img = card.querySelector('[data-pf-card-image] img');
      if (img) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = specs.image;
      }
      // The Step 1 model button's thumbnail follows it too (same image map).
      const modelBtn = this._models.find((b) => Number(b.dataset.pfIndex) === this._activeIndex);
      const btnImg = modelBtn && modelBtn.querySelector('[data-pf-model-thumb] img');
      if (btnImg) {
        btnImg.removeAttribute('srcset');
        btnImg.removeAttribute('sizes');
        btnImg.src = specs.image;
      }
    }
    // New values can change wrap counts — re-level the rows (or, under the
    // rail, re-read the labels: a row may have appeared or gone - Task 35).
    if (this._compareRow) this._syncRailRows();
    this._equalizeCardRows();
  }

  // The selected setup's row and comparison card say what its current choices
  // cost, then where the build starts ("$423 · from $389"; Oct 1 final check
  // P1: the row and card said "From $389" while the price head and Add to cart
  // said $423). Strings come pre-formatted from the card's variant map, in the
  // shop's money format. The other rows keep their "From" (CSS).
  _syncSelectedPrice(entry) {
    if (!entry || typeof entry.price !== 'string') return;
    const index = this._activeIndex;
    [
      this._models.find((b) => Number(b.dataset.pfIndex) === index),
      this._cards.find((c) => Number(c.dataset.pfIndex) === index),
    ].forEach((host) => {
      const sel = host && host.querySelector('[data-pf-price-sel]');
      if (!sel) return;
      const now = sel.querySelector('[data-pf-price-now]');
      const was = sel.querySelector('[data-pf-price-was]');
      const note = sel.querySelector('[data-pf-price-note]');
      if (now) now.textContent = entry.price;
      if (was) {
        was.textContent = entry.was || '';
        was.hidden = !entry.was;
      }
      if (note) note.hidden = !(Number(entry.cents) > Number(sel.dataset.pfFromCents));
    });
  }

  destroy() {
    if (this._holdStop) this._holdStop();
    if (this._spyHandler) {
      window.removeEventListener('scroll', this._spyHandler);
      window.removeEventListener('resize', this._spyHandler);
    }
    if (this._stickyReachObserver) this._stickyReachObserver.disconnect();
    if (this._stickyObserver) this._stickyObserver.disconnect();
    if (this._previewRO) this._previewRO.disconnect();
    if (this._previewMO) this._previewMO.disconnect();
    if (this._onPreviewPing) previewBarWaiters.delete(this._onPreviewPing);
    this._previewRO = this._previewMO = this._onPreviewPing = null;
    // A pending add's 15s busy timer (the cart relay) must not fire on a
    // detached host - an editor section reload (Task 30).
    clearTimeout(this._cartBusyTimer);
    if (this._multilineObserver) this._multilineObserver.disconnect();
    if (this._stickyMQ && this._stickyMQ.removeEventListener) this._stickyMQ.removeEventListener('change', this._onStickyMQ);
    if (this._compareRow && this._onCompareRowScroll) this._compareRow.removeEventListener('scroll', this._onCompareRowScroll);
    if (this._onCompareResize) window.removeEventListener('resize', this._onCompareResize);
    // Bottom spacing is set on <body>, outside this element — release it, or
    // the editor leaves dead padding behind on every section reload.
    document.body.classList.remove('pf-sticky-space');
    document.documentElement.style.removeProperty('--pf-sticky-space');
    if (this._equalizeResizeHandler) window.removeEventListener('resize', this._equalizeResizeHandler);
    if (this._onModelKeydown) {
      const list = this.querySelector('[data-element="model-list"]');
      if (list) list.removeEventListener('keydown', this._onModelKeydown);
    }
    if (this._onPinchStart) {
      this.removeEventListener('touchstart', this._onPinchStart, { capture: true });
      this.removeEventListener('touchmove', this._onPinchMove, { capture: true });
      this.removeEventListener('touchend', this._onPinchEnd, { capture: true });
      this.removeEventListener('modal-close', this._onPinchModalClose);
    }
    if (this._onGalleryTouchStart) {
      this.removeEventListener('touchstart', this._onGalleryTouchStart, { capture: true });
      this.removeEventListener('touchmove', this._onGalleryTouchMove, { capture: true });
      this.removeEventListener('touchend', this._onGalleryTouchEnd, { capture: true });
      this.removeEventListener('touchcancel', this._onGalleryTouchEnd, { capture: true });
    }
    if (this._headerOffsetHandler) {
      window.removeEventListener('scroll', this._headerOffsetHandler);
      window.removeEventListener('resize', this._headerOffsetHandler);
    }
    if (this._galleryFitRO) this._galleryFitRO.disconnect();
    if (this._onGalleryMQ && this._stickyMQ && this._stickyMQ.removeEventListener) this._stickyMQ.removeEventListener('change', this._onGalleryMQ);
    cancelAnimationFrame(this._galleryProgressFrame);
    this._galleryProgressFrame = 0;
    this._galleryMeasurePending = false;
    this._galleryFocusReturn = this._galleryFocusMoved = null;
    window.removeEventListener('resize', this._syncTagline);
    if (this._galleryFitCheck) window.removeEventListener('resize', this._galleryFitCheck);
  }

  _initialActiveIndex() {
    const active = this.querySelector('[data-element="model-option"].\\@active');
    if (!active) return 0;
    return Number(active.dataset.pfIndex) || 0;
  }

  /* ---------------- Model switching ---------------- */

  _onModelListClick(e) {
    // The selected setup's summary line opens its choices (Task 27) and, with
    // them open, folds them away again (Task 28). The same button throughout
    // - open, it moves to the panel's corner - so focus stays on it.
    if (e.target.closest('[data-pf-summary]')) {
      const row = this._models.find((b) => Number(b.dataset.pfIndex) === this._activeIndex);
      if (this.hasAttribute('pf-inplace')) this._foldChoices(row);
      else if (row) this._openChoices(row);
      return;
    }
    const btn = e.target.closest('[data-element="model-option"]');
    if (!btn) return;
    const index = Number(btn.dataset.pfIndex);
    // Phones (Task 24): a tap on the selected setup opens its choices under
    // it - the first tap of a visit may well be on the preselected setup.
    if (index === this._activeIndex) {
      this._openChoices(btn);
      return;
    }
    this._selectModel(index, btn.dataset.pfUrl, btn);
  }

  // Open the selected setup's choices under its row (phones) and glide them
  // into view above the buy bar. An open ends a fold (Task 28), and takes the
  // choices back from the bar's sheet if it holds them.
  _openChoices(row) {
    this._choicesFolded = false;
    const bar = this.querySelector('product-family-sticky');
    if (bar && bar.classList.contains('@open') && typeof bar.collapse === 'function') bar.collapse();
    if (!this._choicesOpen) {
      this._choicesOpen = true;
      this._syncOptionsPlacement();
    }
    this._liftRow(row);
  }

  // Fold the choices back into the summary line (Task 28, Garrett: "I can
  // not choose to collapse it"). The fold holds until the shopper opens the
  // choices again: setups chosen meanwhile arrive folded, with their line.
  _foldChoices(row) {
    this._choicesOpen = false;
    this._choicesFolded = true;
    this._syncOptionsPlacement();
    this._liftRow(row);
  }

  // Next frame, once the change has laid out: glide the selected row - with
  // its choices or its line - clear of the buy bar if it ends under it.
  _liftRow(row) {
    if (!row) return;
    requestAnimationFrame(() => {
      const lift = this._liftFromBar(row, row.getBoundingClientRect().top);
      if (lift > 0) window.scrollBy({ top: lift, left: 0, behavior: this._smooth() });
    });
  }

  // Phones (Task 27, Garrett): the selected setup carries a summary of its
  // choices under its row - "Graphic: Strata +$34 / Setup: 105mm Dad Bod 77a
  // Options", the chosen values with the chips' own price and sold-out
  // notes. Any setup with options gets it (Task 29: one value each too -
  // "Graphic: Strata" on Fathom's Bee's Knees). It is also the choices' toggle
  // (Task 28): folded, the whole line opens them; open, product-family.css
  // shrinks it to "Options" in the panel's corner, on the first option's
  // heading line (the headings name the chosen values), where it folds them.
  // Rebuilt only when its text changes: the options observer calls this on
  // every re-render, and its own update must not feed back.
  _syncSummary(index) {
    const summary = this.querySelector('[data-pf-summary]');
    if (!summary) return;
    const phone = !!(this._stickyMQ && this._stickyMQ.matches);
    const opts = this.querySelector('[data-pf-options]');
    // Phones (Task 29): a setup without options carries its own panel - the
    // deck note and "What's included" - under its row, as desktop's open card
    // does ([pf-fitpanel], product-family.css); its fit copy shows below the
    // list. Only with something to show there.
    const fitPanel = !opts && this.querySelector('[data-pf-fit-panel]');
    this.toggleAttribute('pf-fitpanel', phone && !!fitPanel && !!fitPanel.querySelector('.\\#product-family-deck-note, [data-pf-included]'));
    const on = phone && !!opts && !!opts.querySelector('input[type="radio"]:checked');
    this.toggleAttribute('pf-summary', on);
    summary.setAttribute('aria-expanded', on && this.hasAttribute('pf-inplace') ? 'true' : 'false');
    if (!on) return;
    // Mid-switch _setActiveModel passes the incoming index (_activeIndex is
    // committed after it), so the line moves with the row, as the panel does.
    const active = typeof index === 'number' ? index : this._activeIndex;
    summary.style.order = String(active * 2 + 1);
    const parts = [];
    opts.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      const label = input.closest('label');
      const title = label && label.querySelector('.\\#product-options-radio-item-title');
      if (!title) return;
      const base = Array.from(title.childNodes).filter((n) => n.nodeType === 3).map((n) => n.textContent).join('').trim();
      const notes = Array.from(title.querySelectorAll('[data-pf-delta], [data-pf-soldout]')).map((n) => n.textContent.trim()).filter(Boolean);
      // The stock picker names each option's radios after the option.
      if (base) parts.push({ name: input.name, base, notes });
    });
    const values = summary.querySelector('[data-pf-summary-values]');
    const key = JSON.stringify(parts);
    if (!values || values.dataset.pfKey === key) return;
    values.dataset.pfKey = key;
    // One line per option, named as in the choices (Garrett: "The option
    // names should be shown as well") - "Graphic: Strata +$34".
    values.replaceChildren(...parts.map((part) => {
      const line = document.createElement('span');
      line.className = '#product-family-summary-option';
      if (part.name) {
        const name = document.createElement('span');
        name.className = '#product-family-summary-name';
        name.textContent = part.name + ': ';
        line.appendChild(name);
      }
      line.appendChild(document.createTextNode(part.base));
      part.notes.forEach((note) => {
        const em = document.createElement('em');
        em.textContent = ' ' + note;
        line.appendChild(em);
      });
      return line;
    }));
  }

  _onSelectChange(e) {
    const opt = e.target.selectedOptions[0];
    if (!opt) return;
    // The dropdown sits in the sticky sub-nav, so the shopper may be deep in
    // Features or Specs: hold the section they are reading in place while the
    // regions above it change height (the page used to jump ~70px).
    this._selectModel(Number(e.target.value), opt.dataset.pfUrl, this._readingSection());
  }

  // Where "reading" happens: a line just under the sticky sub-nav.
  _readingLine() {
    const subnav = this.querySelector('[data-element="subnav"]');
    const navBottom = subnav ? Math.max(0, subnav.getBoundingClientRect().bottom) : 0;
    return navBottom + Math.min(200, window.innerHeight * 0.3);
  }

  // The last of this section's parts (overview, comparison, features, specs,
  // reviews) whose top has passed the reading line - static markup, so it
  // survives a model switch. Null above the section.
  _readingSection() {
    const line = this._readingLine();
    let current = null;
    this.querySelectorAll('[data-pf-anchor], .\\#product-family-compare').forEach((s) => {
      if (s.getBoundingClientRect().top <= line) current = s;
    });
    return current;
  }

  // The "It's right for you if..." copy can end by steering to another build
  // ("Want quicker acceleration? The Fathom | Bee's Knees Complete rolls on
  // 90mm wheels"). When that build is on this page, the link selects it in
  // place instead of leaving for its product page, and focus lands on its row
  // (the link itself goes away with the old buy box). Other links - a sibling
  // board - navigate as usual, as do modified clicks.
  _onFitLinkClick(e) {
    const link = e.target.closest('.\\#product-family-fit-body a[href]');
    if (!link || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const path = (u) => {
      try { return new URL(u, window.location.href).pathname.replace(/\/$/, ''); } catch (err) { return null; }
    };
    const want = path(link.getAttribute('href'));
    const model = want && this._models.find((m) => path(m.dataset.pfUrl) === want);
    if (!model) return;
    e.preventDefault();
    const index = Number(model.dataset.pfIndex);
    const done = index === this._activeIndex ? Promise.resolve() : this._selectModel(index, model.dataset.pfUrl, model);
    done.then(() => {
      const row = this._models.find((m) => Number(m.dataset.pfIndex) === index);
      if (row) row.focus({ preventScroll: true });
    });
  }

  _smooth() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  _announce(text) {
    if (!this._live || !text || Date.now() < (this._liveFrom || 0)) return;
    this._live.textContent = '';
    clearTimeout(this._liveTimer);
    this._liveTimer = setTimeout(() => { this._live.textContent = text; }, 150);
  }

  // "Surfskate Edition, $379, Drift / CX" after a model switch; "$389, Drift /
  // 105mm Dad Bod 77a" after an option change; "sold out" when it is.
  async _announceSelection(variantId, withName) {
    const model = this._models.find((m) => Number(m.dataset.pfIndex) === this._activeIndex);
    if (!model) return;
    // A custom build has no variant to buy (its panel holds the Build button,
    // not a form), so it was the one setup switch screen readers didn't hear
    // (Oct 1 final check): its name and price as its row shows them -
    // "Custom Complete, from $389".
    if ('pfCustom' in model.dataset) {
      if (!withName) return;
      const nameEl = model.querySelector('.\\#product-family-model-name');
      const priceEl = model.querySelector('[data-pf-model-price]');
      const said = [];
      if (nameEl) said.push(nameEl.textContent.trim());
      if (priceEl) said.push(priceEl.textContent.trim().replace(/\s+/g, ' ').replace(/^From\b/, 'from'));
      this._announce(said.join(', '));
      return;
    }
    if (!variantId) return;
    const pj = await this._productJson(model.dataset.pfUrl);
    const v = pj && pj.variants && pj.variants.find((x) => String(x.id) === String(variantId));
    const nameEl = model.querySelector('.\\#product-family-model-name');
    const parts = [];
    if (withName && nameEl) parts.push(nameEl.textContent.trim());
    if (v) {
      parts.push(this._formatMoney(v.price));
      if (pj.variants.length > 1 && v.title && v.title !== 'Default Title') parts.push(v.title);
      if (!v.available) parts.push('sold out');
    }
    this._announce(parts.join(', '));
  }

  // Put focus back on the option that was chosen once the picker re-renders.
  _restoreOptionFocus() {
    const k = this._focusKey;
    if (!k || Date.now() - k.at > 5000) return;
    const a = document.activeElement;
    if (a && a !== document.body) return;
    const sel = `[data-pf-options] input[type="radio"][name="${CSS.escape(k.name)}"][value="${CSS.escape(k.value)}"]`;
    const el = Array.from(this.querySelectorAll(sel)).find((x) => {
      const label = x.closest('label') || x.parentElement;
      return label && label.offsetParent !== null;
    });
    if (el) el.focus({ preventScroll: true });
  }

  // Desktop gallery thumbnails and the active image's full-screen view were
  // mouse-only (divs / custom elements without a tab stop). Give them one,
  // a role and a name; Enter / Space click them (_onPfKeydown). Only the
  // active slide's full-screen trigger is a tab stop - the others are off
  // screen. Re-run after every gallery swap and slide change.
  _keyGallery() {
    const g = this.querySelector('[data-pf-region="gallery"]');
    if (!g) return;
    const thumbs = Array.from(g.querySelectorAll('.\\#product-gallery-thumbnails-thumbnail'));
    thumbs.forEach((t, i) => {
      if (!t.hasAttribute('data-pf-keyed')) {
        t.setAttribute('data-pf-keyed', '');
        t.setAttribute('tabindex', '0');
        t.setAttribute('role', 'button');
      }
      // the count can grow while the gallery builds: re-label every pass
      t.setAttribute('aria-label', `Show image ${i + 1} of ${thumbs.length}`);
    });
    const opens = Array.from(g.querySelectorAll('modal-open.\\#product-gallery-media-expand'));
    const activeSlide = g.querySelector('.blaze-active');
    opens.forEach((m) => {
      if (!m.hasAttribute('data-pf-keyed')) {
        m.setAttribute('data-pf-keyed', '');
        m.setAttribute('role', 'button');
        m.setAttribute('aria-label', 'Open the full-screen gallery');
      }
      const isActive = activeSlide ? activeSlide.contains(m) : m === opens[0];
      m.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    // The stock expand circle (phones) is no control while the pinned strip
    // is compact (Task 32): the stylesheet hides it, and it leaves the tab
    // order here too.
    const extend = g.querySelector('.\\#product-gallery-stage-mobile-extend');
    if (extend) {
      if (this.hasAttribute('pf-gallery-compact')) extend.setAttribute('tabindex', '-1');
      else if (extend.getAttribute('tabindex') === '-1') extend.removeAttribute('tabindex');
    }
  }

  _onPfKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = e.target;
    if (!t || !t.matches || !t.matches('[data-pf-region="gallery"] [data-pf-keyed]')) return;
    e.preventDefault();
    // A thumbnail is a label for the slide's (hidden) radio: press the label.
    const label = t.matches('.\\#product-gallery-thumbnails-thumbnail') && t.querySelector('label');
    (label || t).click();
  }

  // "Features: Surfskate Edition": the region headings name the build shown.
  _syncBuildLabels() {
    const model = this._models.find((m) => Number(m.dataset.pfIndex) === this._activeIndex);
    const nameEl = model && model.querySelector('.\\#product-family-model-name');
    const name = nameEl ? nameEl.textContent.trim() : '';
    if (!name) return;
    this.querySelectorAll('[data-pf-build-name]').forEach((el) => { el.textContent = name; });
  }

  // The comparison lives in a <details> that stays open at every width (its
  // summary is hidden): phones swipe the slider again (Task 18; Task 8 had
  // closed it there over a stacked list). Opening it re-measures the slider.
  _initCompareDisclosure() {
    const d = this.querySelector('[data-pf-compare-disclosure]');
    if (!d) return;
    d.open = true;
    d.addEventListener('toggle', () => {
      if (!d.open) return;
      window.dispatchEvent(new Event('resize'));
      requestAnimationFrame(() => {
        this._equalizeCardRows();
        this._showCardInSlider(this._activeIndex);
      });
    });
  }

  // Stars + count beside the price follow Yotpo live: the totals its Reviews
  // widget shows (reviews are pooled per family), from the storefront API
  // the widget itself calls. The server renders the reviews.rating /
  // rating_count metafields Yotpo syncs to each product, which run a few
  // reviews behind (Chinchiller 23 vs 27) - those stay when the request fails
  // or Yotpo has none. One request per product, kept for the page.
  _syncLiveRating() {
    const link = this.querySelector('[data-pf-region="pricehead"] [data-pf-rating-live]');
    if (!link) return;
    const key = link.dataset.pfRatingLive;
    const id = link.dataset.pfRatingProduct;
    if (!key || !id) return;
    this._liveRatings = this._liveRatings || new Map();
    let req = this._liveRatings.get(id);
    if (!req) {
      const url = `https://api-cdn.yotpo.com/v3/storefront/store/${encodeURIComponent(key)}/product/${encodeURIComponent(id)}/reviews?page=1&perPage=1`;
      req = fetch(url)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const b = d && d.bottomline;
          const count = b ? Number(b.totalReview) : 0;
          const score = b ? Number(b.averageScore) : 0;
          return count > 0 && score > 0 ? { count, score } : null;
        })
        .catch(() => null);
      this._liveRatings.set(id, req);
    }
    req.then((live) => {
      if (!live || !link.isConnected) return;
      const max = Number(link.dataset.pfRatingMax) || 5;
      const score = live.score.toFixed(1);
      const noun = live.count === 1 ? 'review' : 'reviews';
      const stars = link.querySelector('[data-pf-rating-stars]');
      const text = link.querySelector('[data-pf-rating-text]');
      const sr = link.querySelector('[data-pf-rating-sr]');
      if (stars) stars.style.setProperty('--pf-rating', `${Math.round((live.score / max) * 1000) / 10}%`);
      if (text) text.textContent = `${score} (${live.count} ${noun})`;
      if (sr) sr.textContent = `Rated ${score} out of ${max} from ${live.count} ${noun}. Go to reviews.`;
      link.hidden = false;
    });
  }


  // ---- Tasks 35 + 36: the comparison as a rail of labels beside the setups ----
  // At every width the cards leave the Blaze track for a native scroll-snap
  // row (the slider stays in the DOM, hidden; the cards are the same nodes, so
  // selection, the variant map, images and prices keep working). With subgrid
  // the host gets [pf-rail]: a sticky label column read from the cards' own
  // labels, every card on the same rows. Phones: 72px rail, two 143px cards
  // per screen, one Learn more under the row for the pick (Task 35). Desktop
  // (Task 36): a 110px rail, the cards sharing the width, each card's own
  // Learn more / Build as a text link on the last row. Without subgrid the
  // cards keep their own labels (two-up on phones). The row's arrows show
  // only when it scrolls (six or more setups on desktop; always on phones).
  _initCompareRow() {
    this._buildCompareRow();
  }

  _buildCompareRow() {
    if (this._compareRow) return;
    const sec = this.querySelector('.\\#product-family-compare');
    const body = sec && sec.querySelector('.\\#product-family-compare-body');
    const slider = body && body.querySelector('slideshow-inline-blaze');
    const track = slider && slider.querySelector('.blaze-track');
    if (!track) return;
    const cards = Array.from(track.children).filter((c) => c.matches('.\\#product-family-card'));
    if (cards.length < 2) return;
    this._compareSlider = slider;
    this._compareCardsOrder = cards;
    const row = document.createElement('div');
    row.className = '#product-family-compare-row';
    row.setAttribute('tabindex', '0');
    row.setAttribute('aria-label', sec.getAttribute('aria-label') || '');
    cards.forEach((c) => row.appendChild(c));
    slider.hidden = true;
    body.appendChild(row);
    this._compareRow = row;
    this.setAttribute('pf-row', '');
    const subgrid = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('grid-template-rows', 'subgrid');
    if (subgrid) this.setAttribute('pf-rail', '');
    // The tools line under the heading: the row's arrows, with the slider's
    // own labels and icons. Hidden whenever every card fits (_syncCompareNav).
    const head = sec.querySelector('.\\#container');
    const tools = document.createElement('div');
    tools.className = '#product-family-compare-tools';
    const navGroup = document.createElement('div');
    navGroup.className = '#product-family-compare-nav-group';
    const mkNav = (dir) => {
      const own = slider.querySelector(dir > 0 ? '.blaze-next' : '.blaze-prev');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = '#product-family-compare-nav';
      b.setAttribute('data-pf-compare-nav', dir > 0 ? 'next' : 'prev');
      b.setAttribute('aria-label', (own && own.getAttribute('aria-label')) || (dir > 0 ? 'Next' : 'Previous'));
      const icon = own && own.querySelector('svg');
      if (icon) b.appendChild(icon.cloneNode(true));
      b.addEventListener('click', () => this._stepCompareRow(dir));
      navGroup.appendChild(b);
      return b;
    };
    this._compareNavPrev = mkNav(-1);
    this._compareNavNext = mkNav(1);
    tools.appendChild(navGroup);
    (head || sec).appendChild(tools);
    this._compareTools = tools;
    // The row's one Learn more (Build on a custom build), following the pick:
    // the phone layout's; desktop shows each card's own link instead (CSS).
    const learn = document.createElement('a');
    learn.className = '#product-family-compare-learn';
    learn.setAttribute('data-pf-compare-learn', '');
    const chev = document.createElement('i');
    chev.className = '#product-family-compare-learn-chevron';
    chev.setAttribute('aria-hidden', 'true');
    learn.append(document.createTextNode(''), chev);
    body.appendChild(learn);
    this._compareLearn = learn;
    this._onCompareRowScroll = () => this._syncCompareNav();
    row.addEventListener('scroll', this._onCompareRowScroll, { passive: true });
    this._onCompareResize = () => this._syncCompareNav();
    window.addEventListener('resize', this._onCompareResize, { passive: true });
    if (subgrid) this._syncRailRows();
    this._equalizeCardRows();
    this._syncCompareLearn();
    requestAnimationFrame(() => {
      this._syncCompareNav();
      this._showCardInSlider(this._activeIndex);
    });
  }

  _compareRows(card) { return Array.from(card.querySelectorAll('.\\#product-family-specs-row')); }
  _rowLabel(row) { const dt = row.querySelector('dt'); return dt ? dt.textContent.trim().replace(/\s+/g, ' ') : ''; }
  _cardName(card) { const t = card.querySelector('.\\#product-family-card-title'); return t ? t.textContent.trim().replace(/\s+/g, ' ') : ''; }

  // The rail and the rows: labels in the order the cards show them (the part
  // rows, then the differing specs), every card's rows on the same grid rows,
  // fill cells where a card lacks one, the card's own link on the last row
  // (shown on desktop). Idempotent: run again after a variant swap reveals or
  // hides a row.
  _syncRailRows() {
    const row = this._compareRow;
    if (!row || !this.hasAttribute('pf-rail')) return;
    const cards = this._compareCardsOrder;
    const FIXED = 6; // photo, name, the switch-failure note's slot, badge, price, best for
    const labels = [];
    cards.forEach((c) => this._compareRows(c).forEach((r) => {
      if (r.hidden) return;
      const k = this._rowLabel(r);
      if (k && labels.indexOf(k) < 0) labels.push(k);
    }));
    const linkRow = FIXED + labels.length + 1;
    row.style.setProperty('--pf-rows', 'repeat(' + linkRow + ', auto)');
    this.querySelectorAll('[data-pf-row-fill]').forEach((f) => f.remove());
    let rail = row.querySelector('.\\#product-family-compare-rail');
    if (!rail) {
      rail = document.createElement('div');
      rail.className = '#product-family-compare-rail';
      rail.setAttribute('aria-hidden', 'true');
    }
    rail.replaceChildren();
    const names = [this._msg('pfMsgCompareSetup', 'Setup'), this._msg('pfMsgComparePrice', 'Price'), this._msg('pfMsgCompareBestFor', 'Best for')].concat(labels).concat(['']);
    names.forEach((n, i) => {
      const s = document.createElement('span');
      s.textContent = n;
      if (i === 0) s.className = '#product-family-compare-rail-head';
      else if (!n) s.className = '#product-family-compare-rail-blank';
      rail.appendChild(s);
    });
    row.insertBefore(rail, row.firstChild);
    const partIdx = ['Trucks', 'Wheels', 'Bearings', 'Bushings'].map((p) => labels.indexOf(p)).filter((i) => i >= 0);
    cards.forEach((c) => {
      const present = {};
      this._compareRows(c).forEach((r) => {
        const k = this._rowLabel(r);
        const i = labels.indexOf(k);
        if (i < 0) return;
        r.style.gridRow = String(FIXED + 1 + i);
        r.setAttribute('data-pf-row', k);
        if (!r.hidden) present[i] = true;
      });
      const deck = c.querySelector('.\\#product-family-card-deck-only');
      if (deck && partIdx.length) {
        deck.style.gridRow = (FIXED + 1 + Math.min.apply(null, partIdx)) + ' / ' + (FIXED + 2 + Math.max.apply(null, partIdx));
        deck.setAttribute('data-pf-row', 'deck');
        partIdx.forEach((i) => { present[i] = true; });
      }
      const link = c.querySelector('.\\#product-family-card-learn-more, .\\#product-family-card-select');
      if (link) { link.style.gridRow = String(linkRow); link.setAttribute('data-pf-row', 'link'); }
      const fill = (gr) => {
        const f = document.createElement('div');
        f.className = '#product-family-card-row-fill';
        f.setAttribute('data-pf-row-fill', '');
        f.style.gridRow = String(gr);
        c.appendChild(f);
      };
      if (!c.querySelector('.\\#product-family-card-best')) fill(FIXED);
      labels.forEach((l, i) => { if (!present[i]) fill(FIXED + 1 + i); });
    });
  }

  _syncCompareLearn() {
    const a = this._compareLearn;
    if (!a) return;
    const sel = this._compareCardsOrder.find((c) => c.classList.contains('@active'));
    if (!sel) { a.hidden = true; return; }
    const link = sel.querySelector('.\\#product-family-card-learn-more, .\\#product-family-card-select');
    const build = !!sel.querySelector('.\\#product-family-card-select');
    a.href = link ? link.getAttribute('href') : (sel.dataset.pfUrl || '#');
    a.hidden = false;
    const tpl = build ? this._msg('pfMsgBuildSetup', 'Build your %name%') : this._msg('pfMsgLearnMoreSetup', 'Learn more about %name%');
    a.firstChild.textContent = tpl.replace('%name%', this._cardName(sel));
  }

  _stepCompareRow(n) {
    const row = this._compareRow;
    if (!row) return;
    const cards = this._compareCardsOrder;
    const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : row.clientWidth;
    row.scrollBy({ left: n * step, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  _syncCompareNav() {
    const row = this._compareRow;
    if (!row || !this._compareNavPrev) return;
    const scrolls = row.scrollWidth > row.clientWidth + 1;
    if (this._compareTools) this._compareTools.hidden = !scrolls;
    this._compareNavPrev.disabled = row.scrollLeft <= 1;
    this._compareNavNext.disabled = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
  }

  // The theme's slider hides its arrows ([hidden]) whenever every card fits
  // the DESKTOP view (slides-per-view), at every width - and since Task 8 a
  // four- or five-card family shows every card on desktop, so phones lost
  // their arrows too. Phones and tablets show 1.15 cards: un-hide the arrows
  // there whenever a card is out of view (the CSS sizes them; the slider's
  // start/end classes fade the one that can't move). Desktop keeps the
  // theme's own rule.
  _initCompareNav() {
    const host = this.querySelector('.\\#product-family-compare slideshow-inline-blaze');
    if (!host) return;
    const mq = window.matchMedia('(max-width: 991px)');
    const apply = () => {
      const slider = host.slider;
      if (!slider) return;
      const total = slider.totalSlides || slider.slides.length;
      const perView = Number(host.getAttribute(mq.matches ? 'slides-per-view-mobile' : 'slides-per-view')) || 1;
      host.querySelectorAll('[data-element="nav"]').forEach((btn) => { btn.hidden = total <= perView; });
    };
    this._whenCompareSliderReady(apply);
    if (mq.addEventListener) mq.addEventListener('change', apply);
  }

  // A click on the card that is already selected takes the shopper to its
  // Add to cart (it used to do nothing).
  _jumpToBuy(index) {
    if (index !== this._activeIndex) return false;
    // A custom build's Build button stands in for Add to cart (Task 22).
    const atc = Array.from(this.querySelectorAll('[data-element="to-cart-button"], [data-pf-build]')).find((b) => b.offsetParent);
    if (!atc) return false;
    // On phones the buy box's button has no box ([sticky-ready]) and the
    // fixed bar's is on screen wherever the page is: focus it where it
    // stands - scrolling it "into view" only nudged the page (Task 30). On
    // desktop DOM order resolves the buy box's button first (bar shown or
    // not), so the scroll below is the desktop path.
    if (!atc.closest('product-family-sticky')) {
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      atc.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
    }
    atc.focus({ preventScroll: true });
    return true;
  }

  _onCompareClick(e) {
    // The card image (compare-media, a real button with the accessible
    // "Select <model>" name) selects the model; the detail page is reached via
    // the card's Learn More link. No scroll: the shopper stays where they are
    // (the card's selected ring and the sticky bar are the feedback).
    const btn = e.target.closest('[data-element="compare-media"]');
    if (btn) {
      if (this._jumpToBuy(Number(btn.dataset.pfIndex))) return;
      const anchor = btn.closest('[data-element="compare-card"]') || btn;
      this._selectModel(Number(btn.dataset.pfIndex), btn.dataset.pfUrl, anchor);
      return;
    }
    // The ENTIRE card is a select target — clicking the title, price, or spec
    // rows picks that model. Interactive children keep their own behavior:
    // the Learn More link navigates, the "See more" accordion toggles.
    // (Build cards carry no compare-card attribute.) Since wave 26.09 the card
    // has no Select button, so the model URL is read off the card itself.
    const card = e.target.closest('[data-element="compare-card"]');
    if (!card) return;
    const inner = e.target.closest('a, details, summary, button');
    if (inner && card.contains(inner)) return;
    if (!card.dataset.pfUrl) return;
    if (this._jumpToBuy(Number(card.dataset.pfIndex))) return;
    this._selectModel(Number(card.dataset.pfIndex), card.dataset.pfUrl, card);
  }

  // anchorEl: keep this element at the same viewport position across the swap.
  // The regions above the click point change height when they re-render (other
  // model = other gallery/options), which would otherwise yank the page.
  async _selectModel(index, url, anchorEl, opts = {}) {
    if ((index === this._activeIndex && !opts.force) || this._switching) return;
    if (!url) return;
    this._switching = true;
    // Visual in-flight state: everything variant-reactive on screen still
    // belongs to the OUTGOING model until the fetched panel swaps in. CSS under
    // [pf-switching] dims and locks those regions so stale options are never
    // presented as current (they used to sit fully interactive for the whole
    // fetch+preload window - very visible on slow connections).
    this.setAttribute('pf-switching', '');
    // Task 33: an earlier attempt's note goes as this one starts (a retry
    // looks like any tap: the row in flight, nothing stale about another
    // setup, and no note can share the tried row's panel slot while the
    // switch lays out). _retrySwitch moves focus off the pill first. What to
    // go back to if the panel never arrives, and whether the buy box had
    // already been swapped when something threw, are captured here; the lock
    // above is released in finally, so no failure can leave the page dimmed.
    this._clearSwitchError();
    const ctx = { prev: this._activeIndex, choicesOpen0: this._choicesOpen, doc: null, buybox: false };
    try {
      await this._switchModel(index, url, anchorEl, ctx);
    } catch (err) {
      console.error('[product-family] setup switch failed:', err);
      if (ctx.buybox) {
        // The incoming buy box (and its form) is in place: keep the selection
        // and make sure the bar's button points at that form, whatever threw
        // in between (the sticky regions swap after the buy box).
        try {
          this._swapRegion('sticky', ctx.doc);
          this._swapRegion('sticky-cta', ctx.doc);
          this._afterSwap();
        } catch (err2) {
          console.error('[product-family] sticky re-swap failed:', err2);
        }
      } else {
        this._failSwitch(index, url, anchorEl, ctx);
      }
    } finally {
      this._switching = false;
      this.removeAttribute('pf-switching');
    }
  }

  // The switch itself (Task 33 split it out of _selectModel so the lock and
  // the failure path live in one try/catch/finally above). Synchronous up to
  // the fetch, as before: the row moves in the tap's own frame.
  async _switchModel(index, url, anchorEl, ctx) {
    // Measure the clicked row / card BEFORE anything moves: _setActiveModel
    // slots the options under the new row at once, shifting every row in
    // between by the options panel's height, and the hold below must pin the
    // position the shopper clicked, not the shifted one.
    const anchorTop0 = anchorEl ? anchorEl.getBoundingClientRect().top : null;
    // Choosing from the list opens the choices under their setup on phones
    // (Task 24) - unless the shopper has folded them away (Task 28); the
    // comparison cards, the sheet and links leave that as it is.
    if (anchorEl && anchorEl.matches && anchorEl.matches('[data-element="model-option"]') && !this._choicesFolded) this._choicesOpen = true;
    this._setActiveModel(index);
    if (anchorEl && anchorTop0 !== null) {
      const shift = anchorEl.getBoundingClientRect().top - anchorTop0;
      if (Math.abs(shift) > 0.5) window.scrollBy({ top: shift, left: 0, behavior: 'instant' });
    }
    // Commit the index BEFORE the region swaps: swapping the buybox re-runs
    // _syncOptionsPlacement(), which would otherwise slot the options under the
    // previously selected model. Re-entry is already blocked by _switching.
    this._activeIndex = index;

    const doc = await this._fetchPanel(url, this._chosenVariant[index], { timeout: PANEL_FETCH_TIMEOUT });
    ctx.doc = doc;
    if (doc) {
      // Warm the incoming panel's images first: swapping with undecoded images
      // makes the gallery re-settle hundreds of px AFTER the swap, yanking the
      // page under the shopper. Time-boxed so a slow image can't stall the UI.
      await this._preloadPanelImages(doc, 'first');
      this._preloadPanelImages(doc);
      const anchorTop = anchorTop0;
      // The shopper's drawer stays open across a model switch. The drawer itself
      // is STATIC section markup (a sibling of the swappable regions), so it
      // survives the swap untouched — but the options node sitting inside it
      // belongs to the OUTGOING model, and the incoming buybox carries its own.
      // Drop the stale one here so exactly one [data-pf-options] exists when
      // _swapRegion('buybox') re-runs _syncOptionsPlacement(), which then hands
      // the new options straight back to the still-open drawer.
      //
      // Collapsing the bar was the previous way to get the node out of the
      // drawer before the swap. It worked, but it closed a drawer the shopper
      // had deliberately opened and bounced the options back into the card.
      // Removing the node achieves the same invariant without touching state
      // the shopper owns.
      const staleOpts = this.querySelector('[data-element="sticky-specs"] [data-pf-options]');
      if (staleOpts) staleOpts.remove();
      REGIONS.forEach((region) => {
        // Flagged BEFORE the swap: a throw in the swap's own tail (the
        // options placement it re-runs) still leaves the new form in place.
        if (region === 'buybox') ctx.buybox = true;
        this._swapRegion(region, doc);
      });
      this._afterSwap();
      this._syncBuildLabels();
      this._keyGallery();
      // The model button and comparison card are static markup that only
      // follow variant CHANGES — point them at the variant just rendered, and
      // record it (normalises an unknown remembered/linked id to the real one).
      const shown = this._currentFormVariantId();
      if (shown) {
        this._lastVariantSync = shown;
        this._chosenVariant[index] = shown;
        this._updateCardVariantSpecs(shown);
      }
      this._saveSelection();
      this._announceSelection(shown, true);
      if (anchorEl && anchorTop !== null) this._holdAnchor(anchorEl, anchorTop);
    } else {
      this._failSwitch(index, url, anchorEl, ctx);
    }
  }

  /* ---------------- Switch recovery (Task 33) ---------------- */

  // The panel never arrived (offline, a 5xx, a request that hung past the
  // timeout). Before Task 33 the new row stayed checked over the OLD buy box,
  // bar and form, so the always-on phone bar added the old setup under the
  // new name, with no word about it. Now the selection goes back to where it
  // was (rows, cards, select and the options' placement all revert through
  // _setActiveModel), the tapped element stays put, a note with a Retry pill
  // shows where the tap happened and glides clear of the phone's buy bar,
  // and the live region says so. Focus is left where it is.
  _failSwitch(index, url, anchorEl, ctx) {
    // A hold from a switch that succeeded moments ago still pins ITS row.
    if (this._holdStop) this._holdStop();
    const held = anchorEl && anchorEl.isConnected ? anchorEl : null;
    const top0 = held ? held.getBoundingClientRect().top : null;
    // A tapped comparison card keeps the slider where it is: the note goes in
    // that card (a slide back to the previous one, then forward again, is
    // dropped by the slider mid-transition and would leave the note off screen).
    const inCard = !!(anchorEl && anchorEl.closest && anchorEl.closest('[data-element="compare-card"]'));
    this._activeIndex = ctx.prev;
    this._choicesOpen = ctx.choicesOpen0;
    this._setActiveModel(ctx.prev, { keepSlide: inCard });
    const message = this._switchErrorMessage(index);
    const note = this._renderSwitchError(index, url, anchorEl, message);
    // One correction, with the note already in place: the panel moving back
    // under the previous row, and a sub-nav notice growing the pinned bar
    // over a held reading section, are both undone here.
    if (held) {
      const shift = held.getBoundingClientRect().top - top0;
      if (Math.abs(shift) > 0.5) window.scrollBy({ top: shift, left: 0, behavior: 'instant' });
    }
    if (note && held) this._revealSwitchError(note, held);
    // A failure is never a restored-selection echo: say it even in the quiet
    // first moments after load (the Back/Forward restore path lands there).
    this._liveFrom = 0;
    this._announce(message);
  }

  // The note: the sold-out help's shape (a sentence, one pill), built fresh
  // for each failure (the attempt that follows clears it). Where it shows is
  // decided by what is on screen: a comparison card the shopper clicked takes
  // it right under its title; the sub-nav select (desktop - the switch
  // control once the list has scrolled away; a Back/Forward restore with no
  // anchor lands there too) takes it as a line under the select, which it
  // describes; a row, or a phone with the select hidden, puts it under the
  // row that was tried, in that row's panel slot - a sibling of the
  // radiogroup, which holds only the setups. Null when there is no host.
  _renderSwitchError(index, url, anchorEl, message) {
    this._clearSwitchError();
    const list = this.querySelector('[data-element="model-list"]');
    const isRow = !!(anchorEl && anchorEl.matches && anchorEl.matches('[data-element="model-option"]'));
    const card = anchorEl && anchorEl.closest ? anchorEl.closest('[data-element="compare-card"]') : null;
    const body = card && card.querySelector('.\\#product-family-card-body');
    const subnavLeft = this.querySelector('.\\#product-family-subnav-left');
    let host = null;
    let before = null;
    if (body && (body.getClientRects().length || (this._compareRow && card.getClientRects().length))) {
      host = body;
      const titleRow = body.querySelector('.\\#product-family-card-title-row');
      before = titleRow ? titleRow.nextSibling : body.firstChild;
    } else if (!isRow && this._select && subnavLeft && subnavLeft.contains(this._select) && subnavLeft.getClientRects().length) {
      host = subnavLeft;
    } else if (list) {
      host = list;
      const group = list.querySelector('.\\#product-family-models-group');
      before = list.querySelector(':scope > [data-pf-summary]') || (group ? group.nextSibling : null);
    }
    if (!host) return null;
    const note = document.createElement('div');
    note.className = '#product-family-switch-error';
    note.setAttribute('data-pf-switch-error', '');
    const text = document.createElement('p');
    text.id = this._switchErrorId();
    text.textContent = message;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = '#product-family-switch-retry';
    retry.textContent = this._msg('pfMsgRetry', 'Retry');
    retry.addEventListener('click', () => this._retrySwitch(retry));
    note.append(text, retry);
    note._pfRetry = { index, url, anchorEl };
    // Model buttons carry order = index * 2, so the odd slot is the row's own.
    if (host === list) note.style.order = String(index * 2 + 1);
    host.insertBefore(note, before);
    if (host === subnavLeft) this._select.setAttribute('aria-describedby', text.id);
    return note;
  }

  // Next frame, once the note has laid out: if it ends under the phone's buy
  // bar or below the viewport, glide the page so it - and its Retry - are in
  // view (Task 23/24's rule for what opens under a tapped row), never lifting
  // the tapped element past the pinned strip. The sub-nav notice is pinned
  // and needs nothing.
  _revealSwitchError(note, anchorEl) {
    requestAnimationFrame(() => {
      if (!note.isConnected || note.closest('[data-element="subnav"]')) return;
      const bar = this.querySelector('product-family-sticky');
      const phoneBar = this.hasAttribute('sticky-mobile') && this._stickyMQ && this._stickyMQ.matches && bar && !bar.hidden && !bar.classList.contains('@open');
      const floor = (phoneBar ? bar.getBoundingClientRect().top : window.innerHeight) - 12;
      const over = note.getBoundingClientRect().bottom - floor;
      if (over <= 0) return;
      const held = anchorEl && anchorEl.isConnected ? anchorEl : note;
      const lift = Math.min(over, held.getBoundingClientRect().top - this._pinnedBottom() - 8);
      if (lift > 0) window.scrollBy({ top: lift, left: 0, behavior: this._smooth() });
    });
  }

  // Retry: the same switch again, from the same place (no force: the
  // selection was put back, so the guard lets it through). The attempt
  // clears the note, so focus is handed over first: to the control being
  // retried - the row, the card's photo button, or the select - and back to
  // the new Retry if this attempt fails too. A second press is swallowed by
  // _switching anyway.
  _retrySwitch(btn) {
    const note = btn.closest('[data-pf-switch-error]');
    const want = note && note._pfRetry;
    if (!want || this._switching) return;
    const hadFocus = document.activeElement === btn;
    if (hadFocus) {
      const target = this._retryFocusTarget(want, !!btn.closest('[data-element="subnav"]'));
      if (target) target.focus({ preventScroll: true });
    }
    this._selectModel(want.index, want.url, want.anchorEl).then(() => {
      if (!hadFocus) return;
      const again = this.querySelector('[data-pf-switch-error] button');
      if (again) again.focus({ preventScroll: true });
    });
  }

  _retryFocusTarget(want, inSubnav) {
    if (inSubnav && this._select) return this._select;
    const card = want.anchorEl && want.anchorEl.closest ? want.anchorEl.closest('[data-element="compare-card"]') : null;
    const media = card && card.querySelector('button.\\#product-family-card-media');
    if (media) return media;
    return this._models.find((b) => Number(b.dataset.pfIndex) === want.index) || null;
  }

  _clearSwitchError() {
    this.querySelectorAll('[data-pf-switch-error]').forEach((n) => n.remove());
    if (this._select && this._select.getAttribute('aria-describedby') === this._switchErrorId()) this._select.removeAttribute('aria-describedby');
  }

  _switchErrorId() {
    return 'pf-switch-error-' + (this.getAttribute('section-id') || 'product-family');
  }

  _switchErrorMessage(index) {
    const name = this._modelName(index) || 'this setup';
    return this._msg('pfMsgSwitchFailed', "Couldn't load %name%. Try again.").replace('%name%', name);
  }

  _modelName(index) {
    const row = this._models.find((b) => Number(b.dataset.pfIndex) === index);
    const el = row && row.querySelector('.\\#product-family-model-name');
    return el ? el.textContent.trim() : '';
  }

  // A string the section translated and handed over as a host attribute
  // (data-pf-msg-*); the literal English when the attribute is missing or
  // the store's locale lacks the key ("Translation missing: ...").
  _msg(key, fallback) {
    const s = this.dataset[key];
    return s && !/ranslation missing/i.test(s) ? s : fallback;
  }

  // An option change whose panel fetch failed (_syncVariantRegions): the form
  // already holds the chosen variant, so the bar shows that variant's price
  // and availability from the product JSON (the gallery and specs stay as
  // they were until the next successful fetch) and the live region announces
  // it as usual. A CTA this element disabled itself for an earlier gap is
  // re-armed from the buy box's own button when the variant is available (a
  // server-rendered Sold out button has no form target to re-arm, so it is
  // left for the next swap); a sold-out variant gets the bar's sold-out
  // label from the buy box, or the stock string when the buy box has none.
  async _mirrorVariantToBar(url, variantId) {
    const pj = await this._productJson(url);
    const v = pj && pj.variants && pj.variants.find((x) => String(x.id) === String(variantId));
    if (v) {
      const price = this._formatMoney(v.price);
      this.querySelectorAll('[data-pf-region="sticky"] [id^="pf-sticky-price2-"], [data-pf-region="sticky-cta"] [id^="pf-sticky-price-"]').forEach((el) => {
        el.textContent = price;
      });
      const cta = this.querySelector('[data-pf-region="sticky-cta"] [data-element="to-cart-button"]');
      const own = this.querySelector('[data-pf-region="buybox"] [data-element="to-cart-button"]');
      if (!v.available) {
        this._setStickyUnavailable();
        if (cta && !this.querySelector('[data-pf-region="buybox"] button[disabled]')) cta.textContent = this._msg('pfMsgSoldOut', 'Sold out');
      } else if (cta && cta.disabled && own && !own.disabled) {
        cta.disabled = false;
        const label = document.createElement('span');
        label.textContent = own.textContent.split('·')[0].trim();
        const priceWrap = document.createElement('span');
        priceWrap.className = '#product-family-sticky-cta-price';
        const sep = document.createElement('span');
        sep.className = '#product-family-sticky-sep';
        sep.setAttribute('aria-hidden', 'true');
        priceWrap.append(sep, price);
        cta.replaceChildren(label, priceWrap);
      }
    }
    this._announceSelection(variantId, false);
  }

  // Keep the tapped row where the shopper tapped it while the regions around
  // it swap and settle (for 2.5s), then - phones - glide it and the choices it
  // opened (Task 24) clear of the buy bar. Task 26 (Oct 6, Garrett: the
  // choices "stutter (shake)" on a phone): the old frame-by-frame hold
  // corrected a late shift above the row (the Shop Pay line re-rendering ~1s
  // after a switch, ~24px) one frame late, while the browser's own scroll
  // anchoring corrected the same shift against its own anchor - the two
  // overshot each other and the row jumped and came back. Now:
  //  - corrections also run in a ResizeObserver callback (after layout,
  //    before paint), so a shift is undone in the frame it happens;
  //  - the browser's scroll anchoring is off while the hold runs, and so is
  //    the root's smooth scroll-behavior (each correction is a plain instant
  //    scroll in every engine);
  //  - the glide is one native smooth scroll (compositor-driven, smooth even
  //    while the swap keeps the main thread busy), started once the incoming
  //    panel has laid out; corrections pause while it runs, then hold the row
  //    where it landed.
  // A wheel or touch ends the hold at once, so it never fights the shopper.
  _holdAnchor(anchorEl, anchorTop) {
    if (this._holdStop) this._holdStop();
    const root = document.documentElement;
    const prev = { behavior: root.style.scrollBehavior };
    // [pf-anchor-hold]: product-family.css switches the browser's scroll
    // anchoring off on every element while the hold runs (Chrome reads
    // overflow-anchor per candidate element, so the root alone isn't enough).
    root.setAttribute('pf-anchor-hold', '');
    root.style.scrollBehavior = 'auto';
    let target = anchorTop;
    let gliding = false;
    let stopped = false;
    const until = Date.now() + 2500;
    const correct = () => {
      if (stopped || gliding || !anchorEl.isConnected) return;
      const delta = anchorEl.getBoundingClientRect().top - target;
      if (Math.abs(delta) <= 0.5) return;
      const y0 = window.scrollY;
      window.scrollBy(0, delta);
      // At the top or bottom of the page the scroll can't follow: hold the
      // row where it is instead of chasing a place it can't reach.
      if (Math.abs(window.scrollY - y0) < 0.5) target = anchorEl.getBoundingClientRect().top;
      // Desktop (Task 33): the swap and this correction land in one frame,
      // with no scroll event between them, so the bar keeps the state the
      // swap evaluated (a comparison-card click could leave the embedded Add
      // to cart cut off under the sub-nav with the bar hidden). Re-read the
      // rule here; the always-on phone bar needs nothing per frame.
      if (this._evaluateSticky && !(this._stickyMQ && this._stickyMQ.matches)) this._evaluateSticky();
    };
    const ro = window.ResizeObserver ? new ResizeObserver(correct) : null;
    if (ro) ro.observe(this);
    const stop = () => {
      if (stopped) return;
      stopped = true;
      if (ro) ro.disconnect();
      root.removeAttribute('pf-anchor-hold');
      root.style.scrollBehavior = prev.behavior;
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      if (this._holdStop === stop) {
        this._holdStop = null;
        this._holdCorrect = null;
        this._holdGliding = null;
      }
    };
    this._holdStop = stop;
    // A region released while the hold runs (the price head, below) corrects
    // in the same step, before the browser paints the shifted frame.
    this._holdCorrect = correct;
    this._holdGliding = () => gliding;
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    const frame = () => {
      if (stopped) return;
      correct();
      if (Date.now() < until) requestAnimationFrame(frame);
      else stop();
    };
    requestAnimationFrame(frame);
    // Two frames in, the incoming panel has laid out: measure, then glide.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (stopped) return;
      correct();
      target = anchorEl.getBoundingClientRect().top;
      const lift = this._liftFromBar(anchorEl, target);
      if (!lift) return;
      const smooth = this._smooth();
      const scroller = document.scrollingElement || root;
      const dest = Math.min(window.scrollY + lift, scroller.scrollHeight - window.innerHeight);
      gliding = true;
      // Landed once the page reaches the glide's end (a scrollend from an
      // earlier instant correction must not count), or after 700ms at most.
      const onEnd = () => { if (Math.abs(window.scrollY - dest) < 2) land(); };
      const land = () => {
        if (!gliding) return;
        gliding = false;
        window.removeEventListener('scrollend', onEnd);
        target = anchorEl.getBoundingClientRect().top;
      };
      window.scrollBy({ top: lift, left: 0, behavior: smooth });
      if (smooth === 'smooth') {
        window.addEventListener('scrollend', onEnd);
        setTimeout(land, 700);
      } else {
        land();
      }
    }));
  }

  // Phones: how far a tapped setup row must rise so it - and the choices it
  // opened under it (Task 24) - sit clear of the buy bar (Task 23: the last
  // rows can arrive under it). Never past the pinned sub-nav and photo; 0 for
  // anything but a row, or with the bar's sheet open.
  _liftFromBar(anchorEl, top0) {
    if (!anchorEl || top0 === null || !anchorEl.matches('[data-element="model-option"]')) return 0;
    if (!this.hasAttribute('sticky-mobile') || !this._stickyMQ || !this._stickyMQ.matches) return 0;
    const bar = this.querySelector('product-family-sticky');
    if (!bar || bar.hidden || bar.classList.contains('@open')) return 0;
    // The row's own height, or - with its choices open under it - from the
    // row's top to the panel's bottom as laid out (the panel's -8px margin
    // only cancels the list's gap); folded, to its summary line's bottom
    // (Task 28); without options, to its own panel's (Task 29).
    const rowRect = anchorEl.getBoundingClientRect();
    const selected = anchorEl.getAttribute('aria-checked') === 'true';
    const end = !selected ? null
      : this.hasAttribute('pf-inplace') ? this.querySelector('[data-pf-options]')
      : this.hasAttribute('pf-summary') ? this.querySelector('[data-pf-summary]')
      : this.hasAttribute('pf-fitpanel') ? this.querySelector('[data-pf-fit-panel]') : null;
    const bottom = top0 + (end ? end.getBoundingClientRect().bottom - rowRect.top : rowRect.height);
    const over = bottom - (bar.getBoundingClientRect().top - 12);
    if (over <= 0) return 0;
    return Math.max(0, Math.min(over, top0 - this._pinnedBottom() - 8));
  }

  // Desktop (Task 33): the viewport line the embedded Add to cart must sit
  // fully below for the page's own button to count as usable - the pinned
  // header and sub-nav (_pinnedBottom) plus the button's height, since the
  // list's bottom edge IS the button's bottom edge (css: the button is the
  // list's last grid item). The bar shows while the list ends above it.
  _desktopStickyLine() {
    const atc = this.querySelector('[data-pf-region="buybox"] [data-element="to-cart-button"], [data-pf-region="buybox"] [data-pf-build]');
    return this._pinnedBottom() + (atc ? atc.getBoundingClientRect().height : 0);
  }

  // Where the bars pinned to the top of a phone screen end once the page has
  // scrolled: the sub-nav under the header, then the photo while it pins
  // (Task 24). --pf-subnav-top tracks the header's bottom edge.
  _pinnedBottom() {
    const subnav = this.querySelector('[data-element="subnav"]');
    let bottom = (parseFloat(this.style.getPropertyValue('--pf-subnav-top')) || 0) + (subnav ? subnav.getBoundingClientRect().height : 0);
    const media = this.hasAttribute('pf-gallery-pin') && this.querySelector('.\\#product-family-overview-media');
    // The strip's compact height (Task 32): while the photo is still
    // collapsing, a tapped row and the strip's bottom edge rise together, so
    // a lift that ends mid-collapse never lands the row under it; past that,
    // the strip is this tall. Its live height when the collapse is off.
    if (media) bottom += (this._galleryDelta > 0 && parseFloat(getComputedStyle(media).getPropertyValue('--pf-gallery-min'))) || media.getBoundingClientRect().height;
    return bottom;
  }

  // The pinned photo's collapse (Task 32). Its progress p - 0 the arrival
  // size, 1 the 120px strip - is how far the photo's flow position has
  // climbed past the sub-nav's bottom edge, over the length it loses. The
  // flow position is read from the overview grid, which never sticks, so p is
  // a function of the scroll alone and cannot feed back on itself. Written on
  // the grid as --pf-gallery-p (the stylesheet sizes the frame, folds the
  // dots row and pays the loss back on the buy column) only when it moved by
  // 0.002, from the scroll frame or after a swap - never from the gallery's
  // own ResizeObserver, where a size change made inside the callback is the
  // browser's "undelivered notifications" loop. pf-gallery-stuck keeps the
  // hairline while the strip holds at the sub-nav; pf-gallery-compact (p
  // from 0.5) trims the strip to the photo alone.
  _measureGalleryCollapse() {
    const grid = this.querySelector('.\\#product-family-overview-grid');
    const media = this.querySelector('.\\#product-family-overview-media');
    const buy = this.querySelector('.\\#product-family-overview-buy');
    if (!grid || !media || !buy) return;
    // A one-image gallery has no dots row to fold.
    const dots = !!media.querySelector('.blaze-pagination');
    if (dots) grid.style.removeProperty('--pf-gallery-dots');
    else if (grid.style.getPropertyValue('--pf-gallery-dots') !== '0px') grid.style.setProperty('--pf-gallery-dots', '0px');
    const cs = getComputedStyle(buy);
    const min = parseFloat(cs.getPropertyValue('--pf-gallery-min')) || 0;
    const dotsH = parseFloat(cs.getPropertyValue('--pf-gallery-dots')) || 0;
    this._galleryFlowPad = parseFloat(getComputedStyle(grid).paddingTop) || 0;
    // The arrival size, resolved by the browser from the same custom property
    // the frame is sized by (min(100%, 280px, 60vh): a percentage, which the
    // script cannot resolve itself) on a zero-height probe kept inside the
    // wrapper - never read back from the wrapper's own height, which the
    // reduced-motion rule sets from this very value (a width change at the
    // top of the page would otherwise read the old size back for good).
    let probe = media.querySelector(':scope > [data-pf-gallery-probe]');
    if (!probe) {
      probe = document.createElement('div');
      probe.setAttribute('data-pf-gallery-probe', '');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = 'position:absolute;top:0;left:0;width:var(--pf-gallery-big);height:0;visibility:hidden;pointer-events:none;';
      media.appendChild(probe);
    }
    const big = probe.getBoundingClientRect().width;
    // At rest the gallery must be the frame and the dots row and nothing more:
    // a slide taller than the square (a vertical video, on a store whose
    // media is not curated) would make the wrapper lose less than the buy
    // column's margin pays back, so the collapse stays off and the photo pins
    // at its own height, as it did before this task.
    const region = media.querySelector('[data-pf-region="gallery"]') || media;
    const atRest = !(this._galleryP > 0);
    if (!(min > 0) || !(big > min) || (atRest && Math.abs(region.getBoundingClientRect().height - (big + dotsH)) > 1.5)) {
      this._galleryDelta = 0;
      grid.style.removeProperty('--pf-gallery-big-px');
      return;
    }
    this._galleryDelta = big - min + dotsH;
    // Reduced motion sizes the wrapper from this (a percentage cannot size a
    // height); written only when it changed.
    const px = big.toFixed(2) + 'px';
    if (grid.style.getPropertyValue('--pf-gallery-big-px') !== px) grid.style.setProperty('--pf-gallery-big-px', px);
  }

  _requestGalleryProgress(measure) {
    if (measure) this._galleryMeasurePending = true;
    if (this._galleryProgressFrame) return;
    this._galleryProgressFrame = requestAnimationFrame(() => {
      this._galleryProgressFrame = 0;
      if (this._galleryMeasurePending) {
        this._galleryMeasurePending = false;
        this._measureGalleryCollapse();
      }
      this._syncGalleryProgress();
    });
  }

  _syncGalleryProgress() {
    const grid = this.querySelector('.\\#product-family-overview-grid');
    const media = this.querySelector('.\\#product-family-overview-media');
    const subnav = this.querySelector('[data-element="subnav"]');
    let p = 0;
    let stuck = false;
    if (grid && media && subnav && this.hasAttribute('pf-gallery-pin') && this._galleryDelta > 0) {
      const T = subnav.getBoundingClientRect().bottom;
      // The wrapper's flow position: the grid's top edge (it is the grid's
      // first item) plus any padding above it.
      const flowTop = grid.getBoundingClientRect().top + (this._galleryFlowPad || 0);
      p = Math.min(1, Math.max(0, (T - flowTop) / this._galleryDelta));
      // The value the stylesheet sees (three decimals): every state below is
      // decided from it, so the dots can never be faded out by the stylesheet
      // while the script still counts them as controls.
      p = p === 0 ? 0 : p === 1 ? 1 : +p.toFixed(3);
      stuck = p > 0 && media.getBoundingClientRect().top >= T - 0.5;
    }
    const was = this._galleryP || 0;
    if (Math.abs(p - was) >= 0.002 || (p !== was && (p === 0 || p === 1))) {
      this._galleryP = p;
      grid.style.setProperty('--pf-gallery-p', p === 0 ? '0' : p === 1 ? '1' : p.toFixed(3));
      // A gallery swap's height hold (_swapRegion) would keep the strip from
      // shrinking while the buy column's margin kept growing under it: let
      // go the moment the collapse starts (the stylesheet sizes the frame
      // now, decoded images or not).
      if (p > 0) {
        const region = this.querySelector('[data-pf-region="gallery"]');
        if (region && region.style.minHeight) region.style.minHeight = '';
      }
    }
    this.toggleAttribute('pf-gallery-stuck', stuck);
    const compact = p >= 0.5;
    if (compact !== this.hasAttribute('pf-gallery-compact')) {
      const active = document.activeElement;
      const region = this.querySelector('[data-pf-region="gallery"]');
      this.toggleAttribute('pf-gallery-compact', compact);
      if (compact) {
        // Focus on a control the compact strip hides (a dot, the expand
        // circle) moves to the strip's own control - the active slide's
        // full-screen trigger, visible at every p, its ring drawn inside the
        // wrapper's clip - rather than vanishing; the region (tabindex -1)
        // only if the gallery has no such trigger. It returns when the dots
        // come back, below.
        if (active && media && media.contains(active) && active.closest('.blaze-pagination, .\\#product-gallery-stage-mobile-extend')) {
          const slide = region && region.querySelector('.blaze-active');
          const open = (slide && slide.querySelector('modal-open.\\#product-gallery-media-expand')) || (region && region.querySelector('modal-open.\\#product-gallery-media-expand'));
          const to = open || region;
          if (to) {
            if (!open) to.setAttribute('tabindex', '-1');
            to.focus({ preventScroll: true });
            this._galleryFocusReturn = active;
            this._galleryFocusMoved = to;
          }
        }
      } else if (this._galleryFocusReturn) {
        const back = this._galleryFocusReturn;
        const moved = this._galleryFocusMoved;
        this._galleryFocusReturn = this._galleryFocusMoved = null;
        if (document.activeElement === moved && back.isConnected && back.getClientRects().length) back.focus({ preventScroll: true });
      }
      this._keyGallery();
    }
  }

  /* ---------------- Selection memory ---------------- */

  // Remember the ACTIVE model's variant. Ignored mid-switch (the outgoing buy
  // box can still publish updates) and for ids that are not this model's.
  _rememberVariant(variantId) {
    if (!variantId || this._switching) return;
    const id = String(variantId);
    const model = this._models.find((b) => Number(b.dataset.pfIndex) === this._activeIndex);
    if (!model) return;
    const known = String(model.dataset.pfVariantIds || '').split(',').filter(Boolean);
    if (known.length && known.indexOf(id) === -1) return;
    this._chosenVariant[this._activeIndex] = id;
    this._saveSelection();
  }

  // Keep the selection in this page's own history entry. Same URL, so nothing
  // records a new page view; Back/Forward and reload bring the entry back.
  _saveSelection() {
    try {
      const key = this.getAttribute('section-id') || 'product-family';
      const state = Object.assign({}, history.state);
      state.pfSelection = Object.assign({}, state.pfSelection, {
        [key]: { i: this._activeIndex, v: this._chosenVariant[this._activeIndex] || this._currentFormVariantId() || null },
      });
      history.replaceState(state, '');
    } catch (err) {
      // History unavailable (sandboxed frame): in-page memory still works.
    }
  }

  // On load: a selection saved in the history entry wins; otherwise a link
  // with ?setup=<product handle> (optional &variant=<id>) picks the model.
  // Read-only: the URL is never rewritten.
  _restoreSelection() {
    let want = null;
    try {
      const key = this.getAttribute('section-id') || 'product-family';
      const saved = history.state && history.state.pfSelection && history.state.pfSelection[key];
      if (saved && typeof saved.i === 'number') want = { index: saved.i, variant: saved.v };
    } catch (err) {
      want = null;
    }
    if (!want) {
      const params = new URLSearchParams(window.location.search);
      const handle = (params.get('setup') || '').trim().toLowerCase();
      if (handle) {
        const hit = this._models.find((b) => (b.dataset.pfUrl || '').split('?')[0].replace(/\/$/, '').toLowerCase().endsWith('/products/' + handle));
        if (hit) want = { index: Number(hit.dataset.pfIndex), variant: params.get('variant') };
      }
    }
    if (!want) return;
    const btn = this._models.find((b) => Number(b.dataset.pfIndex) === want.index);
    if (!btn || !btn.dataset.pfUrl) return;
    if (want.variant) this._chosenVariant[want.index] = String(want.variant);
    if (want.index === this._activeIndex && (!want.variant || String(want.variant) === this._currentFormVariantId())) return;
    this._selectModel(want.index, btn.dataset.pfUrl, null, { force: true });
  }

  /* ---------------- Option annotations ---------------- */

  // Product JSON per product URL: price and availability of every variant.
  _productJson(url) {
    const base = String(url || '').split('?')[0];
    if (!base) return Promise.resolve(null);
    this._productCache = this._productCache || {};
    if (!this._productCache[base]) {
      this._productResolved = this._productResolved || {};
      this._productCache[base] = fetch(`${base}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => { if (json) this._productResolved[base] = json; return json; })
        .catch(() => null);
    }
    return this._productCache[base];
  }

  _formatMoney(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const digits = cents % 100 ? 2 : 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits,
      }).format(cents / 100);
    } catch (err) {
      return '$' + (cents / 100).toFixed(digits);
    }
  }

  // Option pills say what they change: the price difference against the
  // current selection, and a readable "Sold out" (the stock picker only fades
  // the pill). A sold-out selection offers the in-stock alternatives one
  // choice away. Idempotent per rendered label: the provider re-renders the
  // picker on every variant change and the MutationObserver re-runs this.
  async _annotateOptions() {
    const model = this._models && this._models.find((b) => Number(b.dataset.pfIndex) === this._activeIndex);
    if (!model) return;
    const product = await this._productJson(model.dataset.pfUrl);
    if (product) this._annotateWith(product);
  }

  // The stock picker renders each option as an h2 heading ("Graphic: Strata")
  // over loose radios: screen readers heard "Strata, radio button, 1 of 2" with
  // no "Graphic", and the headings sat in the page outline. Make each option a
  // radio group named by its heading, take the heading out of the outline, and
  // mark one-value options so CSS shows them as text. Idempotent; re-run on
  // every re-render (from the options observer).
  _labelOptionGroups() {
    const box = this.querySelector('[data-pf-options]');
    if (!box) return;
    box.querySelectorAll('.\\#product-variant-selector-item').forEach((item, i) => {
      const heading = item.querySelector('.\\#product-variant-selector-item-heading');
      const group = item.querySelector('.\\#product-options-radio');
      if (!heading || !group) return;
      if (!heading.id) heading.id = `pf-opt-h-${this.getAttribute('section-id') || 'pf'}-${i}`;
      if (heading.getAttribute('role') !== 'none') heading.setAttribute('role', 'none');
      if (group.getAttribute('role') !== 'radiogroup') group.setAttribute('role', 'radiogroup');
      if (group.getAttribute('aria-labelledby') !== heading.id) group.setAttribute('aria-labelledby', heading.id);
      const single = group.querySelectorAll('input[type="radio"]').length === 1;
      if (single !== item.hasAttribute('data-pf-single')) item.toggleAttribute('data-pf-single', single);
    });
  }

  // Same, without waiting: called from the options observer as soon as the
  // picker re-renders, so the un-annotated pills are never painted (their
  // labels popping in a frame later moved them under the pointer).
  _annotateOptionsNow() {
    const model = this._models && this._models.find((b) => Number(b.dataset.pfIndex) === this._activeIndex);
    const base = model && String(model.dataset.pfUrl || '').split('?')[0];
    const product = base && this._productResolved && this._productResolved[base];
    if (product) this._annotateWith(product);
  }

  _annotateWith(product) {
    const box = this.querySelector('[data-pf-options]');
    if (!box || !product || !product.variants || !box.isConnected) return;
    const currentId = String(this._currentFormVariantId() || '');
    const current = product.variants.find((v) => String(v.id) === currentId);
    const stamp = currentId || 'none';
    const labels = Array.from(box.querySelectorAll('label.\\#product-options-radio-item'));
    // Differences are shown against the lowest price each option row can reach
    // with the other options as chosen ("Strata +$34", "Drift"): picking within
    // a row then changes no pill's label, so no pill changes width.
    const rowBase = {};
    labels.forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      const target = input && product.variants.find((v) => String(v.id) === String(input.dataset.variantId));
      if (!target) return;
      if (rowBase[input.name] === undefined || target.price < rowBase[input.name]) rowBase[input.name] = target.price;
    });
    labels.forEach((label) => {
      if (label.dataset.pfAnnotated === stamp) return;
      label.dataset.pfAnnotated = stamp;
      const input = label.querySelector('input[type="radio"]');
      const title = label.querySelector('.\\#product-options-radio-item-title');
      if (!input || !title) return;
      title.querySelectorAll('[data-pf-delta], [data-pf-soldout]').forEach((n) => n.remove());
      const target = product.variants.find((v) => String(v.id) === String(input.dataset.variantId));
      if (!target) return;
      const d = target.price - (rowBase[input.name] === undefined ? target.price : rowBase[input.name]);
      if (d > 0) {
        const tag = document.createElement('span');
        tag.dataset.pfDelta = '';
        tag.className = '#product-family-option-delta';
        tag.textContent = '+' + this._formatMoney(d);
        title.appendChild(tag);
      }
      if (!target.available) {
        const so = document.createElement('span');
        so.dataset.pfSoldout = '';
        so.className = '#product-family-option-soldout';
        so.textContent = 'Sold out';
        title.appendChild(so);
      }
    });
    this._renderSoldOutHelp(box, product, current);
  }

  _renderSoldOutHelp(box, product, current) {
    let help = box.querySelector('[data-pf-soldout-help]');
    if (!current || current.available) {
      if (help) help.remove();
      return;
    }
    const alts = [];
    const seen = new Set();
    (product.options || []).forEach((opt, i) => {
      product.variants.forEach((v) => {
        if (!v.available || !v.options || v.options[i] === current.options[i]) return;
        if (!v.options.every((val, j) => j === i || val === current.options[j])) return;
        const key = i + ':' + v.options[i];
        if (seen.has(key)) return;
        seen.add(key);
        alts.push({ index: i, value: v.options[i] });
      });
    });
    const sig = current.id + '|' + alts.map((a) => a.index + ':' + a.value).join(',');
    if (help && help.dataset.pfSig === sig) return;
    if (!help) {
      help = document.createElement('div');
      help.dataset.pfSoldoutHelp = '';
      help.className = '#product-family-soldout-help';
      box.insertBefore(help, box.querySelector('[data-pf-fit], .\\#product-family-option-help'));
    }
    help.dataset.pfSig = sig;
    const text = document.createElement('p');
    text.textContent = alts.length ? `${current.title} is sold out. In stock:` : `${current.title} is sold out.`;
    const parts = [text];
    if (alts.length) {
      const list = document.createElement('div');
      list.className = '#product-family-soldout-alts';
      alts.forEach((a) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = '#product-family-soldout-alt';
        b.textContent = a.value;
        b.addEventListener('click', () => {
          const input = Array.from(box.querySelectorAll('input[type="radio"]'))
            .find((inp) => inp.value === a.value && Number(inp.dataset.optionIndex) === a.index);
          if (input) (input.closest('label') || input).click();
        });
        list.appendChild(b);
      });
      parts.push(list);
    }
    help.replaceChildren(...parts);
  }

  _setActiveModel(index, opts = {}) {
    this._models.forEach((btn) => {
      const on = Number(btn.dataset.pfIndex) === index;
      btn.classList.toggle('@active', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    this._cards.forEach((card) => {
      const on = Number(card.dataset.pfIndex) === index;
      card.classList.toggle('@active', on);
      const media = card.querySelector('[data-element="compare-media"]');
      if (media) media.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (this._compareRow) this._syncCompareLearn();
    if (this._select && Number(this._select.value) !== index) {
      this._select.value = String(index);
    }
    // The closed control now shows a different title — refit its font.
    this._fitModelSelect();
    // Slot the variant options under the newly selected model. _activeIndex is
    // only assigned after the swap completes, so pass the index explicitly.
    this._syncOptionsPlacement(index);
    this._syncModelTabindex(index);
    // Keep the Model Options row showing the selected model (not when a
    // failed switch from a card puts its note in that card - Task 33).
    if (!opts.keepSlide) this._showCardInSlider(index);
  }

  // Shrink overlong titles in the sub-nav model dropdown. A native <select>
  // clips silently, so per-option we compute the font-size that fits and set
  // it inline (honored inside the open popup everywhere except the OS-native
  // pickers on macOS/iOS), and the control itself gets the SELECTED option's
  // size — only a too-long title shrinks; short ones render at full size.
  // Measure-only: reads the select's box, never resizes it.
  _fitModelSelect() {
    const select = this._select;
    if (!select || !select.options.length) return;
    // Clear our own inline size first or it feeds back into baseSize.
    select.style.fontSize = '';
    const style = getComputedStyle(select);
    const baseSize = parseFloat(style.fontSize);
    // Inner text space: content box minus padding and the native arrow.
    const avail = select.clientWidth
      - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 30;
    if (!baseSize || avail <= 0) return;
    if (!this._fitCanvas) this._fitCanvas = document.createElement('canvas');
    const ctx = this._fitCanvas.getContext('2d');
    ctx.font = `${style.fontStyle} ${style.fontWeight} ${baseSize}px ${style.fontFamily}`;
    const MIN_SCALE = 0.7;
    Array.from(select.options).forEach((opt) => {
      const width = ctx.measureText(opt.text.trim()).width;
      if (width > avail) {
        const scale = Math.max(avail / width, MIN_SCALE);
        opt.style.fontSize = (baseSize * scale).toFixed(2) + 'px';
      } else {
        opt.style.fontSize = '';
      }
    });
    const selected = select.options[select.selectedIndex];
    select.style.fontSize = (selected && selected.style.fontSize) || '';
  }

  // Load a fetched panel's images into the browser cache (with the same
  // srcset/sizes, so the exact candidates the DOM will pick get warmed).
  // scope 'first': only the gallery's first image - what a switch shows at
  // once (the idle prefetch, for every build). scope 'gallery' (default): the
  // gallery and the sticky bar's thumb - a switch, or a build the shopper is
  // pointing at. Feature photos and the rest lazy-load on scroll as usual
  // (warming every region's images for every build cost 2-3MB on arrival).
  _preloadPanelImages(doc, scope = 'gallery') {
    const jobs = [];
    const seen = new Set();
    let imgs;
    if (scope === 'first') imgs = [doc.querySelector('[data-pf-region="gallery"] img')].filter(Boolean);
    else imgs = Array.from(doc.querySelectorAll('[data-pf-region="gallery"] img, [data-pf-region="sticky"] img'));
    imgs.forEach((img) => {
      const key = (img.getAttribute('srcset') || '') + '|' + (img.getAttribute('src') || '');
      if (!img.getAttribute('src') || seen.has(key)) return;
      seen.add(key);
      jobs.push(new Promise((resolve) => {
        const im = new Image();
        im.onload = resolve;
        im.onerror = resolve;
        if (img.getAttribute('sizes')) im.sizes = img.getAttribute('sizes');
        if (img.getAttribute('srcset')) im.srcset = img.getAttribute('srcset');
        im.src = img.getAttribute('src');
      }));
    });
    if (!jobs.length) return Promise.resolve();
    return Promise.race([
      Promise.all(jobs),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  }

  // Warm the panel cache (and its images) for the non-active models during
  // idle time after window load, so a model switch usually needs no network
  // round-trip. Without this, the first click on each model waits a full
  // fetch + image preload while the outgoing model's options sit on screen -
  // the visible "wrong options for a second" lag on slow connections.
  // Sequential (one panel at a time), lowest priority, skipped for shoppers on
  // Save-Data or 2G, and inert if the element left the DOM (section reload).
  _prefetchPanels() {
    if (this.hasAttribute('no-cache')) return;
    const conn = navigator.connection;
    if (conn && (conn.saveData || /(^|[^3-9])2g/.test(conn.effectiveType || ''))) return;
    const urls = this._models
      .map((btn) => btn.dataset.pfUrl)
      .filter((u, i, a) => u && a.indexOf(u) === i);
    // Non-active models first (most likely next click); the active model's
    // panel LAST - its content is already inline, but returning to it after
    // switching away needs the fetched panel, so warm it too.
    const active = this._models.find((btn) => Number(btn.dataset.pfIndex) === this._activeIndex);
    const activeUrl = active && active.dataset.pfUrl;
    const queue = urls.filter((u) => u !== activeUrl);
    if (activeUrl) queue.push(activeUrl);
    if (!queue.length) return;
    const start = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
      idle(async () => {
        for (const url of queue) {
          if (!this.isConnected) return;
          const doc = await this._fetchPanel(url);
          if (doc && this.isConnected) await this._preloadPanelImages(doc, 'first');
        }
      });
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    this._initIntentWarm();
  }

  // A build the shopper points at, focuses or touches (its row or its
  // comparison card) is the likely next pick: warm its whole gallery then,
  // once per build, instead of every build's on arrival.
  _initIntentWarm() {
    if (this._intentWarmOn) return;
    this._intentWarmOn = true;
    this._warmed = new Set();
    const warm = (e) => {
      const el = e.target && e.target.closest && e.target.closest('[data-element="model-option"], [data-element="compare-card"]');
      if (!el) return;
      const index = Number(el.dataset.pfIndex);
      if (Number.isNaN(index) || index === this._activeIndex || this._warmed.has(index)) return;
      this._warmed.add(index);
      const model = this._models.find((m) => Number(m.dataset.pfIndex) === index);
      if (!model || !model.dataset.pfUrl) return;
      this._fetchPanel(model.dataset.pfUrl, this._chosenVariant[index]).then((doc) => {
        if (doc && this.isConnected) this._preloadPanelImages(doc);
      });
    };
    this.addEventListener('pointerover', warm, { passive: true });
    this.addEventListener('focusin', warm);
    this.addEventListener('touchstart', warm, { passive: true });
  }

  // Fetch a product's panel HTML via the family-panel alternate template.
  // Pass variantId to render the panel with that variant selected (drives the
  // variant's curated gallery set). Cached per (product, variant); a failure
  // is never cached, so a Retry really refetches. opts.timeout (ms, Task 33):
  // give up after that long - set by the switch and the option sync, which
  // have a shopper waiting and a message to show; not by the warms.
  async _fetchPanel(url, variantId, opts = {}) {
    const base = url.split('?')[0];
    // The view belongs to the setup row that owns this product (Task 10): the
    // custom build's row names the panel's custom view in data-pf-view (the
    // section's Custom product block is the only custom signal); every other
    // row takes the host's panel-view. Every caller - switch, option sync,
    // dropdown, card, deep link, prefetch, warm - comes through here, and the
    // cache is keyed by view too, so one product's two renders never share.
    const row = (this._models || []).find((m) => String(m.dataset.pfUrl || '').split('?')[0] === base);
    const view = (row && row.dataset.pfView) || this.getAttribute('panel-view') || 'family-panel';
    const key = `${view}|${base}` + (variantId ? `::${variantId}` : '');
    if (this._panelCache[key]) return this._panelCache[key];
    const noCache = this.hasAttribute('no-cache');
    let fetchUrl = `${base}?view=${encodeURIComponent(view)}`;
    if (variantId) fetchUrl += `&variant=${encodeURIComponent(variantId)}`;
    const ctl = opts.timeout > 0 && typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), opts.timeout) : 0;
    try {
      const res = await fetch(fetchUrl, { headers: { 'X-Requested-With': 'fetch' }, signal: ctl ? ctl.signal : undefined });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      if (!noCache) this._panelCache[key] = doc;
      return doc;
    } catch (err) {
      console.error('[product-family] panel fetch failed:', err);
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  _swapRegion(region, doc) {
    const src = doc.querySelector(`[data-pf-region="${region}"]`);
    const dest = this.querySelector(`[data-pf-region="${region}"]`);
    if (!src || !dest) return;
    // Incoming content already carries the stable "product-family-panel" scope
    // (buybox provider/listeners) and the panel section id (gallery), matching the
    // inline render — so we do NOT rewrite section-id here.
    const nodes = Array.from(src.childNodes).map((n) => document.importNode(n, true));
    // Hold the gallery's height across the swap until its new images have
    // decoded: an empty frame collapses and re-expands, shoving everything
    // below it (measured: 0.86 CLS for one graphic change on a phone).
    const hold = region === 'gallery' || region === 'pricehead' ? dest.getBoundingClientRect().height : 0;
    if (hold && region === 'pricehead') {
      // The price head is held at its height, clipped, until its Shop Pay
      // line has re-rendered (Task 26): that line arrives empty, then one or
      // two lines tall, a beat after the swap, and its shifts reached the
      // screen before anything could correct them (the "shake").
      dest.style.height = hold + 'px';
      dest.style.overflow = 'hidden';
    } else if (hold) {
      dest.style.minHeight = hold + 'px';
    }
    dest.replaceChildren(...nodes);
    if (region === 'pricehead') this._syncLiveRating();
    // The pinned photo's collapse follows the new gallery (Task 32): its
    // sizes re-measured and its progress re-applied in the next frame.
    if (region === 'gallery') this._requestGalleryProgress(true);
    if (hold) {
      if (region === 'gallery') this._releaseHold(dest);
      else this._releaseWhenSettled(dest, hold);
    }
    // Keep the region's own data-* state (media count / curated flag) in sync too.
    Array.from(src.attributes).forEach((attr) => {
      if (attr.name.indexOf('data-pf-') === 0) dest.setAttribute(attr.name, attr.value);
    });
    // A buybox swap replaces the provider, and with it the options node — put
    // the fresh one back under the selected model.
    if (region === 'buybox') {
      this._syncOptionsPlacement();
    }
    // A freshly-swapped head row always arrives with a collapsed chevron; the
    // drawer (section-owned) keeps whatever state it had, so re-sync the two.
    // 'sticky-cta' matters too: it carries the custom build's Build link
    // (Task 22; per-store label, Task 8), whose presence feeds the empty-drawer check.
    if (region === 'sticky' || region === 'sticky-cta') {
      const bar = this.querySelector('product-family-sticky');
      if (bar && typeof bar.syncToggle === 'function') bar.syncToggle();
      // The chevron lives INSIDE the 'sticky' region, so a swap replaces it —
      // re-apply the empty-drawer state to the new button, or a hidden chevron
      // comes back on every model switch.
      this._syncStickyDrawer();
      // A fresh sticky-cta arrives without the busy state an in-flight add
      // put on the old button (Task 30): put it back, or the new button
      // reads as tappable while the submit guard still holds every submit.
      if (region === 'sticky-cta' && this._resyncStickyBusy) this._resyncStickyBusy();
    }
  }

  // The mobile drawer's content IS the live options block (moved in by
  // _syncOptionsPlacement), so there is nothing to render here — only the
  // question of whether the chevron has anything to reveal. An empty drawer
  // should not advertise itself: hide the chevron when the model has no
  // options (a custom build has none; Task 22 also removed the drawer's
  // per-model secondary button, so nothing else can fill it).
  _syncStickyDrawer() {
    const opts = this.querySelector('[data-pf-options]');
    // The sheet holds the selected setup's choices only (Task 25), so with
    // nothing to choose - no options, or options of one value each (shown as
    // text) - there is no chevron: the sheet would only repeat the bar.
    const empty = !this._hasChoice(opts);
    this.querySelectorAll('[data-element="sticky-toggle"]').forEach((t) => { t.hidden = empty; });
    // Guarded on @open: collapse() re-enters this method via the bar's
    // _setOpen -> _syncOptionsPlacement chain, and would otherwise recurse.
    if (empty) {
      const bar = this.querySelector('product-family-sticky');
      if (bar && bar.classList.contains('@open') && typeof bar.collapse === 'function') bar.collapse();
    }
    this._syncStickySpace();
  }

  // Whether the selected setup has a choice to make: an option with two or
  // more values (the stock picker names each option's radios alike).
  _hasChoice(opts) {
    if (!opts) return false;
    const count = {};
    opts.querySelectorAll('input[type="radio"][name]').forEach((i) => { count[i.name] = (count[i.name] || 0) + 1; });
    return Object.keys(count).some((name) => count[name] > 1);
  }

  // Where the variant options live. Normally they stay put inside the provider
  // and CSS `order` positions them under the selected model — the node must not
  // leave the provider, whose re-render map is built by querying its own
  // subtree in the fetched document.
  //
  // The one exception is the open mobile drawer, which genuinely needs the node
  // moved into it. That is safe because it is user-initiated long after the
  // provider has cached, and _selectModel collapses the drawer (returning the
  // node) before any region swap can replace the provider underneath it.
  // Release a swap-time min-height once the region's content is back to its
  // old height, has stopped changing for 600ms, or after 2s at most.
  // Release the price head's swap-time height once its content has settled:
  // the Shop Pay line rendered and nothing moved for 250ms (2.5s at most),
  // never mid-glide. Released in this step together with the hold's
  // correction, so the row the shopper tapped never paints out of place.
  _releaseWhenSettled(dest, hold) {
    cancelAnimationFrame(dest._pfSettleFrame);
    const t0 = performance.now();
    let last = -1;
    let stableSince = t0;
    const contentHeight = () => {
      const kids = Array.from(dest.children).filter((k) => k.getClientRects().length);
      if (!kids.length) return 0;
      const top = dest.getBoundingClientRect().top;
      return Math.max(...kids.map((k) => k.getBoundingClientRect().bottom)) - top;
    };
    const release = () => {
      dest.style.height = '';
      dest.style.overflow = '';
      dest.style.minHeight = '';
      if (this._holdCorrect) this._holdCorrect();
    };
    const tick = () => {
      if (!dest.isConnected) return;
      const h = Math.round(contentHeight());
      const now = performance.now();
      if (h !== last) { last = h; stableSince = now; }
      const terms = dest.querySelector('shopify-payment-terms');
      const ready = !terms || terms.getBoundingClientRect().height > 0;
      const gliding = this._holdGliding && this._holdGliding();
      if ((!gliding && ready && now - stableSince > 250) || now - t0 > 2500) {
        release();
        return;
      }
      dest._pfSettleFrame = requestAnimationFrame(tick);
    };
    dest._pfSettleFrame = requestAnimationFrame(tick);
  }

  // Release a swap-time min-height once the region's first images have
  // decoded (time-boxed, so a slow image never pins the layout).
  _releaseHold(dest) {
    clearTimeout(dest._pfHoldTimer);
    const imgs = Array.from(dest.querySelectorAll('img')).slice(0, 3);
    const jobs = imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    })));
    let done = false;
    const release = () => {
      if (done) return;
      done = true;
      clearTimeout(dest._pfHoldTimer);
      requestAnimationFrame(() => { dest.style.minHeight = ''; });
    };
    Promise.all(jobs).then(release);
    dest._pfHoldTimer = setTimeout(release, 1500);
  }

  _syncOptionsPlacement(index) {
    const active = typeof index === 'number' ? index : this._activeIndex;
    const opts = this.querySelector('[data-pf-options]');
    // A model without options can still carry its "right for you" panel,
    // which hangs under the selected model the same way (it never moves).
    const fitPanel = this.querySelector('[data-pf-fit-panel]');
    if (fitPanel) fitPanel.style.order = String(active * 2 + 1);
    if (!opts) {
      this.removeAttribute('pf-inplace');
      this.toggleAttribute('options-attached', !!fitPanel);
      this._syncStickyDrawer();
      this._syncSummary(active);
      return;
    }

    const bar = this.querySelector('product-family-sticky');
    const drawer = this.querySelector('[data-element="sticky-specs"]');
    const open = !!bar && bar.classList.contains('@open');

    if (open && drawer) {
      if (opts.parentElement !== drawer) drawer.appendChild(opts);
      opts.style.order = '';
    } else {
      const meta = this.querySelector('[data-pf-region="buybox"] [data-element="product-meta"]');
      if (meta && opts.parentElement !== meta) {
        meta.insertBefore(opts, meta.querySelector('.\\#product-family-buybox-atc'));
      }
      // Model buttons carry order = index * 2, so odd slots sit between them.
      opts.style.order = String(active * 2 + 1);
    }
    // 'options-attached' means the panel is VISUALLY attached under the
    // selected model button — placement, not existence. While the open mobile
    // drawer borrows the node the button must render as a standalone selected
    // button (closed ring, rounded corners), not with an open bottom edge.
    this.toggleAttribute('options-attached', !(open && drawer));
    // Phones (Task 24): once a tap has opened the choices they hang from the
    // selected row; until then, and while the sheet holds them, they follow
    // the list. Desktop CSS ignores this - it always attaches.
    this.toggleAttribute('pf-inplace', !!this._choicesOpen && !(open && drawer));
    this._syncStickyDrawer();
    this._syncSummary(active);
  }

  // An always-on fixed bar (mobile sticky mode) covers the bottom of the page,
  // footer included — reserve its height on <body>. Measured from the COLLAPSED
  // bar: the drawer is transient, and reserving its height would leave a gap
  // under the page whenever it is closed.
  // Task 34. The lift is the preview toolbar's wrapper height (68px on
  // phones, read, not assumed), applied only after the toolbar's frame has
  // pinged (see the module-level listener), re-read when the wrapper resizes
  // (orientation) and dropped when it leaves. The legacy bottom iframe
  // (#preview-bar-iframe: no wrapper, no ping) is measured directly.
  _initPreviewLift() {
    this._previewOffset = '';
    const box = () => {
      const frame = document.querySelector(PREVIEW_BAR_FRAME);
      if (!frame) return null;
      const wrapper = frame.closest('#PBarNextFrameWrapper');
      if (wrapper) return previewBarPinged ? wrapper : null;
      return frame.id === 'preview-bar-iframe' ? frame : null;
    };
    const apply = () => {
      const b = box();
      const h = b && getComputedStyle(b).display !== 'none' ? Math.round(b.getBoundingClientRect().height) : 0;
      // Only a strip along the bottom edge counts (a wrapper the size of the
      // viewport, or one placed elsewhere, is not a toolbar to clear).
      const r = b && b.getBoundingClientRect();
      const next = h > 0 && h < 200 && r && Math.abs(r.bottom - window.innerHeight) < 2 ? h + 'px' : '';
      if (next === this._previewOffset) return;
      this._previewOffset = next;
      const bar = this.querySelector('product-family-sticky');
      if (bar) bar.style.bottom = next;
      this._syncStickySpace();
    };
    this._applyPreviewLift = apply;
    // Follow the wrapper's size and its coming and going.
    const watch = () => {
      const b = box();
      if (b && !this._previewRO && typeof ResizeObserver === 'function') {
        this._previewRO = new ResizeObserver(apply);
        this._previewRO.observe(b);
      }
    };
    this._previewMO = new MutationObserver(() => { watch(); apply(); });
    this._previewMO.observe(document.body, { childList: true });
    this._onPreviewPing = () => { watch(); apply(); };
    previewBarWaiters.add(this._onPreviewPing);
    watch();
    apply();
  }

  _syncStickySpace() {
    const bar = this.querySelector('product-family-sticky');
    const on = this.hasAttribute('sticky-mobile') && this._stickyMQ && this._stickyMQ.matches && bar;
    if (!on) {
      document.body.classList.remove('pf-sticky-space');
      document.documentElement.style.removeProperty('--pf-sticky-space');
      return;
    }
    if (!bar.classList.contains('@open')) {
      // Plus the preview-toolbar lift (Task 34), so the page's end and the
      // pinned photo's room check clear the toolbar as well as the bar.
      const h = Math.round(bar.getBoundingClientRect().height) + (parseFloat(this._previewOffset) || 0);
      if (h > 0) document.documentElement.style.setProperty('--pf-sticky-space', h + 'px');
    }
    document.body.classList.add('pf-sticky-space');
    // The pinned photo's room check counts the bar (Task 24).
    if (this._galleryFitCheck) this._galleryFitCheck();
  }

  _afterSwap() {
    // The buybox now belongs to another model/product — re-seed the gap
    // follower with the incoming variant id, so the fresh provider's
    // initialization republish doesn't read as a smart-select correction
    // (which would redundantly re-swap regions that were just swapped).
    this._lastVariantSync = this._currentFormVariantId();
    // The scroll-spy observer watches the stable overview wrapper (only the
    // inner regions swap), so it stays attached. The sticky bar node IS
    // replaced, so re-evaluate its visibility against the current scroll.
    if (this._evaluateSticky) this._evaluateSticky();
    this._refreshYotpo();
  }

  // The reviews region now holds a fresh Yotpo widget for the newly-selected
  // product; ask Yotpo to (re-)initialise it. Guarded so a missing/loading
  // Yotpo never throws.
  _refreshYotpo() {
    try {
      const y = window.yotpo;
      if (y && typeof y.refreshWidgets === 'function') y.refreshWidgets();
    } catch (err) {
      /* no-op */
    }
  }

  /* ---------------- Sub-nav: smooth scroll + scroll-spy ---------------- */

  _onAnchorClick(e) {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    e.preventDefault();
    // Set the clicked anchor active immediately and hold the scroll-spy while the
    // smooth scroll settles — otherwise short sections (e.g. Specs) let the spy's
    // mid-viewport band land on the NEXT section and steal the active state.
    if (link.dataset.pfTarget) {
      this._setActiveAnchor(link);
      this._spyHoldUntil = Date.now() + 1200;
    }
    target.scrollIntoView({ behavior: this._smooth(), block: 'start' });
  }

  _setActiveAnchor(link) {
    this.querySelectorAll('[data-element="anchor"]').forEach((a) => {
      const on = a === link;
      a.classList.toggle('!active', on);
      if (on) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  }

  // The active tab is the last tabbed section whose top has passed the reading
  // line under the sub-nav. The comparison has no tab and sits inside
  // Overview's span, so Overview stays lit over it (a 5% band at mid-screen
  // used to skip short sections and leave Features lit over Specs). At the end
  // of the page the last section wins even if it is too short to reach the line.
  _initScrollSpy() {
    const anchors = Array.from(this.querySelectorAll('[data-element="anchor"]'));
    if (!anchors.length) return;
    const pairs = anchors
      .map((a) => ({ a, s: this.querySelector(`[data-pf-anchor="${a.dataset.pfTarget}"]`) }))
      .filter((p) => p.s);
    if (!pairs.length) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      if (Date.now() < (this._spyHoldUntil || 0)) return;
      const line = this._readingLine();
      let current = pairs[0];
      pairs.forEach((p) => { if (p.s.getBoundingClientRect().top <= line) current = p; });
      const last = pairs[pairs.length - 1];
      const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atEnd && last.s.getBoundingClientRect().top < window.innerHeight) current = last;
      this._setActiveAnchor(current.a);
    };
    this._spyHandler = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', this._spyHandler, { passive: true });
    window.addEventListener('resize', this._spyHandler, { passive: true });
    update();
  }

  /* ---------------- Sticky add-to-cart visibility ---------------- */

  _initStickyVisibility() {
    // Trigger off the model list: its last grid item is the Add-to-cart button,
    // so the bar appears the moment the ATC scrolls off the top of the viewport
    // (not after the whole hero has passed). The list is section markup, stable
    // across model/variant swaps, so observing it is safe. Falls back to the
    // overview wrapper if it is missing.
    // NOT the buybox region: its wrappers are display:contents (they flow into
    // the model grid), so the region has no box of its own - IntersectionObserver
    // and getBoundingClientRect both read 0x0 and the desktop bar never showed
    // (found in the 2026-09-28 critique; broken on production too).
    const overview = this.querySelector('[data-pf-anchor="overview"]');
    const trigger = this.querySelector('[data-element="model-list"]') || overview;
    const sticky = this.querySelector('product-family-sticky');
    if (!trigger || !sticky) return;

    // In theme-preview sessions, Shopify's preview toolbar overlays the bottom
    // of the viewport; lift the bar above it (no effect on the live site: no
    // toolbar, no lift). Task 34: measured from the toolbar's wrapper once the
    // toolbar has drawn, not a 48px guess at init - the wrapper is there even
    // when nothing draws inside it, and the bar then floated over an empty
    // strip with the first setup row showing through (Garrett's phone, Oct 7).
    this._initPreviewLift();

    // Show the sticky bar once the hero/buy area has scrolled above the top.
    // (Re-applied on every evaluation because model switches replace the bar node.)
    const setSticky = (scrolledPast) => {
      const bar = this.querySelector('product-family-sticky');
      if (!bar) return;
      if (this._previewOffset) bar.style.bottom = this._previewOffset;
      // Mobile sticky mode: this bar is the ONLY Add to cart, so it never hides.
      // The hidden ATTRIBUTE is removed rather than overridden in CSS for two
      // reasons: core.css declares [hidden]{display:none!important}, which no
      // author rule can outrank, and `hidden` also removes the bar from the
      // accessibility tree — which would leave screen-reader users with no way
      // to add to cart. [sticky-ready] then lets the CSS hide the buy box's own
      // button; it follows the reveal HERE (set with it, dropped with the hide
      // below - Task 30), so the native button stays whenever the bar is not
      // there: a broken script, or the page above this section.
      if (this.hasAttribute('sticky-mobile') && this._stickyMQ && this._stickyMQ.matches) {
        // ...but it waits until this section is on screen: above it the page
        // can be about another board (an archived model's page introduces its
        // successor here), and the bar would sell this family before the page
        // has said what it is. On a family's own page the section starts on
        // the first screen, so nothing changes there.
        const reached = !overview || overview.getBoundingClientRect().top < window.innerHeight;
        // The bar's button may hold focus as the bar goes (back above the
        // section on an archived model's page): hand it to the buy box's own
        // button, shown again by [sticky-ready]'s removal, instead of letting
        // it fall to the body (Task 30). Attribute first, then the hide.
        const handoff = !reached && bar.contains(document.activeElement);
        this.toggleAttribute('sticky-ready', reached);
        bar.hidden = !reached;
        if (handoff) {
          const native = this.querySelector('[data-pf-region="buybox"] [data-element="to-cart-button"]');
          if (native) native.focus({ preventScroll: true });
        }
        // Only now does the bar have a measurable height to reserve.
        if (reached) this._syncStickySpace();
        return;
      }
      this.removeAttribute('sticky-ready');
      bar.hidden = !scrolledPast;
    };
    // Desktop (Task 33): the list's last grid item is the embedded Add to
    // cart and the sticky header + sub-nav cover the top of the viewport, so
    // "scrolled past" is read against that stack: the bar shows while any
    // part of the button sits under it (_desktopStickyLine; a bar appearing
    // beats scrolling the page - Garrett, Oct 6). The line moves with the
    // header, so the rule is re-read on every scroll frame (the header
    // offset handler) and after each anchor-hold correction; the observer
    // only triggers the same rule, never its own geometry, so the two can
    // never disagree. The phone branch of setSticky ignores the value.
    this._evaluateSticky = () => setSticky(trigger.getBoundingClientRect().bottom < this._desktopStickyLine());

    this._stickyObserver = new IntersectionObserver(
      () => this._evaluateSticky(),
      { threshold: 0, rootMargin: `-${Math.max(0, Math.round(this._desktopStickyLine()))}px 0px 0px 0px` }
    );
    this._stickyObserver.observe(trigger);
    // The phone rule above needs a call when the section first comes on
    // screen, which the model-list observer does not see.
    if (overview && overview !== trigger) {
      this._stickyReachObserver = new IntersectionObserver(() => this._evaluateSticky(), { threshold: 0 });
      this._stickyReachObserver.observe(overview);
    }
    // Evaluate once NOW (Task 30), not in the observers' first callback - a
    // later rendering frame, 130-290ms after DOMContentLoaded, during which
    // the page had no Add to cart at all. On phones this shows the bar, sets
    // sticky-ready and writes --pf-sticky-space in the same task the module
    // runs, before _initStickyGallery's first pin check reads the bar's
    // height. Only once the bar's stylesheet has applied (the section loads
    // it through a blocking link, so it has): a bar un-hidden unstyled would
    // paint in flow and reserve its unstyled height. Otherwise the init rAF
    // in render() makes the call.
    if (getComputedStyle(sticky).position === 'fixed') {
      this._stickyEvaluated = true;
      this._evaluateSticky();
    }
  }
});

/* ---------------- Cart result relay ---------------- */
// The buy box's product form publishes cart events under the panel view's
// section id, not the family section's; this listener carries that id and
// hands the result to the family's live region (review M4).
customElements.define('product-family-cart-relay', class extends Core {
  render() {
    const host = this.closest('product-family');
    if (!host || !Events) return;
    // The sticky Add to cart is the only buy path on phones (Task 30) and sits
    // outside product-to-cart, which gives only its own button a busy state:
    // a second tap during a slow add added the board twice. Busy here is the
    // theme's own `!loading` class (the spinner; pointer-events off) plus
    // aria-busy on the bar's button - never `disabled`, which is the
    // Unavailable state's signal (_setStickyUnavailable) and would drop focus
    // to the body - and the submit guard below holds every further submit
    // until the add has answered.
    const cta = () => host.querySelector('[data-pf-region="sticky-cta"] [data-element="to-cart-button"]');
    const setBusy = (on) => {
      const btn = cta();
      if (!btn) return;
      btn.classList.toggle('!loading', on);
      if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
    };
    const pending = () => host._cartPendingAt && Date.now() - host._cartPendingAt < 15000;
    const clear = () => {
      clearTimeout(host._cartBusyTimer);
      host._cartPendingAt = 0;
      setBusy(false);
      // A gap pill tapped during the add emptied the form's id and mirrored
      // Unavailable onto the button (whose label write dropped the spinner):
      // mirror it again so the cleared button cannot read as an Add to cart.
      if (!host._currentFormVariantId()) host._setStickyUnavailable();
    };
    // A sticky-cta region swapped in during the add (a setup switch, an
    // option change) arrives without the busy state: _swapRegion asks for
    // it back through this.
    host._resyncStickyBusy = () => { if (pending()) setBusy(true); };
    // The guard, and where the busy state is armed: capture phase on the
    // host, which outlives the swappable form, for its add-to-cart form only
    // (any other form inside the host - a review form - passes). Armed HERE
    // rather than on PRODUCT_FORM_SUBMIT because that event is published
    // under the panel scope every product-family section on a page shares,
    // so one section's add would arm another's guard; a submit event is this
    // section's by construction. This listener runs before product-form's
    // own (bound on the form), so a submit during a pending add never
    // reaches it and never publishes a second add.
    host.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form && form.matches && form.matches('form[data-type="add-to-cart-form"]'))) return;
      if (pending()) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      host._cartPendingAt = Date.now();
      setBusy(true);
      clearTimeout(host._cartBusyTimer);
      // Nothing answered in 15s (a dead connection): release the button and
      // say so - a silently re-armed button names neither the problem nor
      // the recovery. Literal here like the two announcements below.
      host._cartBusyTimer = setTimeout(() => { clear(); host._announce('Add to cart did not finish. Please try again.'); }, 15000);
    }, true);
    // The theme's cart provider answers under its own section, so the result
    // is heard globally - and only acted on for an add this section asked for
    // in the last few seconds. The theme's bus keeps the FIRST subscription
    // per event per component (a later sub for the same event is ignored),
    // so everything lives inside these two handlers.
    if (Events.CART_UPDATE) {
      this.sub(Events.CART_UPDATE, (data) => {
        // The add's own answer carries req 'cartAdd'; a cart:refresh update
        // carries no req and is not this add succeeding.
        if (!pending() || !(data && data.req === 'cartAdd')) return;
        clear();
        host._announce('Added to cart.');
        // The phone sheet closes on a successful add (Oct 1 final check P1:
        // left open behind the cart, it covered ~60% of the screen with Add to
        // cart under the thumb, and the next tap added the board again).
        const bar = host.querySelector('product-family-sticky');
        if (bar && bar.classList.contains('@open') && typeof bar.collapse === 'function') bar.collapse();
      }, { global: true });
    }
    if (Events.CART_ERROR) {
      this.sub(Events.CART_ERROR, (err) => {
        if (!pending()) return;
        clear();
        const msg = err && (err.message || err.description);
        host._announce('Not added to cart' + (typeof msg === 'string' && msg ? ': ' + msg : '.'));
      }, { global: true });
    }
  }
});

/* ---------------- Gallery numeric counter ---------------- */
customElements.define('product-family-gallery', class extends Core {
  render() {
    this._counter = this.querySelector('[data-element="gallery-counter-current"]');
    if (!this._counter) return;
    const evt = Events && Events.PRODUCT_GALLERY_SLIDE_CHANGE;
    if (!evt) return;
    this.sub(evt, (index) => {
      const n = (typeof index === 'number' ? index : (index && index.index) || 0) + 1;
      this._counter.textContent = String(n);
      const host = this.closest('product-family');
      if (host && host._keyGallery) requestAnimationFrame(() => host._keyGallery());
    });
  }
});

/* ---------------- "Learn more" description toggle ---------------- */
customElements.define('product-family-toggle', class extends Core {
  render() {
    const trigger = this.querySelector('[data-element="toggle-trigger"]');
    const body = this.querySelector('[data-element="toggle-body"]');
    if (!trigger || !body) return;
    trigger.addEventListener('click', () => {
      const collapsed = body.getAttribute('data-collapsed') !== 'false';
      body.setAttribute('data-collapsed', collapsed ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
      const more = trigger.querySelector('[data-toggle-more]');
      const less = trigger.querySelector('[data-toggle-less]');
      if (more) more.hidden = collapsed;
      if (less) less.hidden = !collapsed;
    });
  }
});

/* ---------------- Sticky bar expand/collapse ---------------- */
customElements.define('product-family-sticky', class extends Core {
  render() {
    // DELEGATED, not bound to the button: the bar's head row is a swappable
    // region, so the toggle element is replaced on every model/variant change
    // while this element — and the drawer beside it — persist. A directly
    // bound listener would die on the first model switch.
    this._onClick = (e) => {
      const trigger = e.target.closest('[data-element="sticky-toggle"]');
      if (!trigger || !this.contains(trigger)) return;
      this._setOpen(!this.classList.contains('@open'));
    };
    this.addEventListener('click', this._onClick);
    this._setOpen(false);
  }

  // The open state lives as a class on THIS element (which survives swaps);
  // the drawer's hidden attribute and the chevron's aria-expanded follow it.
  _setOpen(on) {
    this.classList.toggle('@open', on);
    const panel = this.querySelector('[data-element="sticky-panel"]');
    if (panel) panel.hidden = !on;
    this.syncToggle();
    // The drawer shows the live variant options; the host owns moving them.
    const host = this.closest('product-family');
    if (host && typeof host._syncOptionsPlacement === 'function') host._syncOptionsPlacement();
  }

  // Re-apply the current state to the chevron. Called after a head-row swap,
  // where a freshly rendered toggle always arrives with aria-expanded="false".
  syncToggle() {
    const on = this.classList.contains('@open');
    this.querySelectorAll('[data-element="sticky-toggle"]').forEach((t) => {
      t.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  collapse() { this._setOpen(false); }

  destroy() {
    if (this._onClick) this.removeEventListener('click', this._onClick);
  }
});